import { z } from 'zod';
import { QualityFailureSchema, ScenarioExecutionHistoryItemSchema } from './failures';
import { ProductionIntelSchema } from './production-intel';
import { QUALITY_RISK_SEVERITIES } from './quality-hq';
import { MaintenanceItemSchema, MaintenanceOwnershipQueueSchema } from './change-review-maintenance';
import { MaintenanceDraftHintSchema } from './maintenance-draft';
import { LocatorAutomationImpactSchema } from './locator-automation-impact';
import { TestDataImpactIntelligenceSchema } from './test-data-impact';

/**
 * Scenario Repository — one quality objective, many validation methods.
 *
 * Canonical location:
 * Application → Domain → Module → Business Flow → Scenario
 * Manual / API / UI / E2E are coverage on the scenario, not folders.
 */

export const SCENARIO_VIEWS = ['all', 'manual', 'automated', 'exploratory', 'archived'] as const;
export type ScenarioView = (typeof SCENARIO_VIEWS)[number];

export const SCENARIO_HEALTH = ['healthy', 'needs_review', 'outdated', 'at_risk', 'archived'] as const;
export type ScenarioHealth = (typeof SCENARIO_HEALTH)[number];

export const SCENARIO_STATUS = [
  'draft',
  'review',
  'approved',
  'active',
  'needs_review',
  'obsolete',
  'archived',
] as const;
export type ScenarioStatus = (typeof SCENARIO_STATUS)[number];

export const SCENARIO_PRIORITY = ['low', 'medium', 'high', 'critical'] as const;
export type ScenarioPriority = (typeof SCENARIO_PRIORITY)[number];

/** Lightweight requirement link embedded on scenarios. */
export const LinkedRequirementSchema = z.object({
  id: z.string().uuid(),
  key: z.string().min(1).max(80),
  title: z.string().max(200),
});
export type LinkedRequirement = z.infer<typeof LinkedRequirementSchema>;

export const SCENARIO_AUTOMATION_STATUS = ['none', 'partial', 'automated'] as const;
export type ScenarioAutomationStatus = (typeof SCENARIO_AUTOMATION_STATUS)[number];

/** Authoring intent for automation coverage on create — not the persisted status. */
export const SCENARIO_AUTOMATION_INTENT = ['manual', 'automated', 'hybrid'] as const;
export type ScenarioAutomationIntent = (typeof SCENARIO_AUTOMATION_INTENT)[number];

export const SCENARIO_STEP_RESULT = ['untested', 'passed', 'failed', 'blocked', 'skipped'] as const;
export type ScenarioStepResultStatus = (typeof SCENARIO_STEP_RESULT)[number];

export const SCENARIO_EXECUTION_STATUS = [
  'queued',
  'in_progress',
  'passed',
  'failed',
  'blocked',
  'skipped',
  'cancelled',
] as const;
export type ScenarioExecutionStatus = (typeof SCENARIO_EXECUTION_STATUS)[number];

/** Statuses a tester can close a run with by hand, instead of letting the steps derive it. */
export const SCENARIO_EXECUTION_RESULT = ['passed', 'failed', 'blocked', 'skipped'] as const;
export type ScenarioExecutionResult = (typeof SCENARIO_EXECUTION_RESULT)[number];

export const SCENARIO_EVIDENCE_KIND = ['screenshot', 'video', 'file'] as const;
export type ScenarioEvidenceKind = (typeof SCENARIO_EVIDENCE_KIND)[number];

export const SCENARIO_ENVIRONMENTS = ['QA', 'Staging', 'Production'] as const;
export type ScenarioEnvironment = (typeof SCENARIO_ENVIRONMENTS)[number];

export const SCENARIO_BROWSERS = ['Chrome', 'Firefox', 'Safari', 'Edge'] as const;
export type ScenarioBrowser = (typeof SCENARIO_BROWSERS)[number];

/** data: URL cap, matching the manual-cycle attachment budget. */
export const MAX_SCENARIO_EVIDENCE_BYTES = 1_500_000;
export const MAX_SCENARIO_ATTACHMENTS = 10;
export const BLOCKED_SCENARIO_ATTACHMENT_EXTS = ['.exe', '.dll', '.sh', '.msi', '.deb', '.bat'] as const;

export const ScenarioEvidenceSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(SCENARIO_EVIDENCE_KIND),
  name: z.string().min(1).max(200),
  contentType: z.string().min(1).max(120),
  dataUrl: z.string().min(1).max(MAX_SCENARIO_EVIDENCE_BYTES),
  bytes: z.number().int().nonnegative(),
});
export type ScenarioEvidence = z.infer<typeof ScenarioEvidenceSchema>;

export const ScenarioCoverageSchema = z.object({
  manual: z.boolean(),
  api: z.boolean(),
  ui: z.boolean(),
  e2e: z.boolean(),
});
export type ScenarioCoverage = z.infer<typeof ScenarioCoverageSchema>;

export const SCENARIO_STEP_KIND = ['given', 'when', 'then', 'and', 'but'] as const;
export type ScenarioStepKind = (typeof SCENARIO_STEP_KIND)[number];

/** Authoring-time step verdict before/during manual runs (Requirement #2). */
export const STRUCTURED_STEP_RESULT = ['not_run', 'running', 'passed', 'failed', 'blocked', 'skipped'] as const;
export type StructuredStepResult = (typeof STRUCTURED_STEP_RESULT)[number];

/** Whether a step is exercised manually or via automation coverage. */
export const STRUCTURED_STEP_TYPE = ['manual', 'automation'] as const;
export type StructuredStepType = (typeof STRUCTURED_STEP_TYPE)[number];

export const GHERKIN_KEYWORD_LABEL: Record<ScenarioStepKind, string> = {
  given: 'Given',
  when: 'When',
  then: 'Then',
  and: 'And',
  but: 'But',
};

export const STEP_AUTOMATION_MODES = ['manual', 'automated', 'not_automated'] as const;
export type StepAutomationMode = (typeof STEP_AUTOMATION_MODES)[number];

export const STEP_AUTOMATION_ACTIONS = [
  'navigate',
  'click',
  'fill',
  'select',
  'upload',
  /** Maps to canvas `download-file` (SCENARIO_AUTOMATION_CANVAS_ACTIONS + B1 runtime). */
  'download',
  'hover',
  'wait',
  'assert',
  'api_request',
  /** Maps to canvas `screenshot` (SCENARIO_AUTOMATION_CANVAS_ACTIONS). */
  'screenshot',
  /** Maps to canvas `check` — force checkbox/radio checked (P1-1). */
  'check',
  /** Maps to canvas `uncheck` — force checkbox/radio unchecked (P1-1). */
  'uncheck',
] as const;
export type StepAutomationAction = (typeof STEP_AUTOMATION_ACTIONS)[number];

export const ScenarioStepAutomationSchema = z.object({
  mode: z.enum(STEP_AUTOMATION_MODES).default('not_automated'),
  action: z.enum(STEP_AUTOMATION_ACTIONS).optional(),
  locator: z.string().max(500).default(''),
  timeoutMs: z.number().int().min(0).max(600_000).optional(),
  retry: z.number().int().min(0).max(10).optional(),
  screenshot: z.boolean().default(false),
  continueOnFailure: z.boolean().default(false),
  stepType: z.string().max(80).default(''),
});
export type ScenarioStepAutomation = z.infer<typeof ScenarioStepAutomationSchema>;

export const SCENARIO_TYPES = ['manual', 'automated', 'hybrid'] as const;
export type ScenarioType = (typeof SCENARIO_TYPES)[number];

export const SCENARIO_LIST_AUTOMATION = ['not_automated', 'automated', 'failed'] as const;
export type ScenarioListAutomation = (typeof SCENARIO_LIST_AUTOMATION)[number];

export const ScenarioRequirementsSchema = z.object({
  userStory: z.string().max(2_000).default(''),
  requirement: z.string().max(500).default(''),
  acceptanceCriteria: z.string().max(4_000).default(''),
  externalId: z.string().max(120).default(''),
  scenarioType: z.enum(SCENARIO_TYPES).optional(),
  ownerName: z.string().max(120).default(''),
  reviewerName: z.string().max(120).default(''),
});
export type ScenarioRequirements = z.infer<typeof ScenarioRequirementsSchema>;

export const ScenarioStepSchema = z.object({
  id: z.string().uuid(),
  order: z.number().int().positive(),
  kind: z.enum(SCENARIO_STEP_KIND).optional(),
  action: z.string().min(1).max(500),
  expectedResult: z.string().max(500).default(''),
  /** Legacy alias — kept in sync with `testData`. */
  inputData: z.string().max(2_000).default(''),
  testData: z.string().max(2_000).default(''),
  actualResult: z.string().max(2_000).nullable().default(null),
  result: z.enum(STRUCTURED_STEP_RESULT).default('not_run'),
  stepType: z.enum(STRUCTURED_STEP_TYPE).default('manual'),
  /** Legacy alias — kept in sync with `comment`. */
  comments: z.string().max(2_000).default(''),
  comment: z.string().max(2_000).default(''),
  automationFlowId: z.string().uuid().nullable().optional(),
  automation: ScenarioStepAutomationSchema.optional(),
  attachments: z.array(ScenarioEvidenceSchema).max(MAX_SCENARIO_ATTACHMENTS).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScenarioStep = z.infer<typeof ScenarioStepSchema>;

export const SCENARIO_SEQUENCE_MODEL = ['step', 'component_reference'] as const;
export type ScenarioSequenceModel = (typeof SCENARIO_SEQUENCE_MODEL)[number];

export const ScenarioComponentReferenceSchema = z.object({
  stepModel: z.literal('component_reference'),
  id: z.string().uuid(),
  order: z.number().int().positive(),
  componentId: z.string().uuid(),
  componentKey: z.string().min(1).max(80),
  componentName: z.string().max(200).default(''),
  parameters: z.record(z.string(), z.string()).default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScenarioComponentReference = z.infer<typeof ScenarioComponentReferenceSchema>;

export const ScenarioSequenceItemSchema = z.union([ScenarioComponentReferenceSchema, ScenarioStepSchema]);
export type ScenarioSequenceItem = z.infer<typeof ScenarioSequenceItemSchema>;

export function isScenarioComponentReference(item: ScenarioSequenceItem): item is ScenarioComponentReference {
  return 'stepModel' in item && item.stepModel === 'component_reference';
}

export function isScenarioNormalStep(item: ScenarioSequenceItem): item is ScenarioStep {
  return !isScenarioComponentReference(item);
}

/** Inbound component reference for create/patch. */
export const ScenarioComponentReferenceInputSchema = z.object({
  stepModel: z.literal('component_reference'),
  id: z.string().uuid().optional(),
  order: z.number().int().positive().optional(),
  componentId: z.string().uuid(),
  componentKey: z.string().min(1).max(80).optional(),
  componentName: z.string().max(200).optional(),
  parameters: z.record(z.string(), z.string()).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type ScenarioComponentReferenceInput = z.infer<typeof ScenarioComponentReferenceInputSchema>;

/** Inbound step shape for create/patch — `normalizeSteps` fills defaults. */
export const ScenarioStepInputSchema = z.object({
  id: z.string().uuid().optional(),
  order: z.number().int().positive().optional(),
  kind: z.enum(SCENARIO_STEP_KIND).optional(),
  keyword: z.enum(SCENARIO_STEP_KIND).optional(),
  action: z.string().min(1).max(500),
  expectedResult: z.string().max(500).optional(),
  inputData: z.string().max(2_000).optional(),
  testData: z.string().max(2_000).optional(),
  actualResult: z.string().max(2_000).nullable().optional(),
  result: z.enum(STRUCTURED_STEP_RESULT).optional(),
  stepType: z.enum(STRUCTURED_STEP_TYPE).optional(),
  comments: z.string().max(2_000).optional(),
  comment: z.string().max(2_000).optional(),
  automationFlowId: z.string().uuid().nullable().optional(),
  automation: ScenarioStepAutomationSchema.optional(),
  attachments: z.array(ScenarioEvidenceSchema).max(MAX_SCENARIO_ATTACHMENTS).optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type ScenarioStepInput = z.infer<typeof ScenarioStepInputSchema>;

export const ScenarioSequenceInputSchema = z.union([ScenarioComponentReferenceInputSchema, ScenarioStepInputSchema]);
export type ScenarioSequenceInput = z.infer<typeof ScenarioSequenceInputSchema>;

export function createStructuredStepId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`;
}

/** Fill Requirement #2 defaults on a partial step (stable id, result, timestamps). */
export function finalizeScenarioStep(
  partial: Partial<ScenarioStep> & Pick<ScenarioStep, 'action'>,
  order: number,
  now = new Date().toISOString(),
): ScenarioStep {
  const testData = (partial.testData ?? partial.inputData ?? '').trim();
  const comment = (partial.comment ?? partial.comments ?? '').trim();
  const kind = partial.kind;
  const resultRaw = partial.result;
  const result =
    resultRaw === 'running' ||
    resultRaw === 'passed' ||
    resultRaw === 'failed' ||
    resultRaw === 'blocked' ||
    resultRaw === 'skipped' ||
    resultRaw === 'not_run'
      ? resultRaw
      : 'not_run';
  const stepTypeRaw = partial.stepType;
  const stepType = stepTypeRaw === 'automation' || stepTypeRaw === 'manual' ? stepTypeRaw : 'manual';
  return {
    id: partial.id ?? createStructuredStepId(),
    order,
    kind,
    action: partial.action.trim(),
    expectedResult: (partial.expectedResult ?? '').trim(),
    inputData: testData,
    testData,
    actualResult: partial.actualResult ?? null,
    result,
    stepType,
    comments: comment,
    comment,
    automationFlowId: partial.automationFlowId ?? null,
    automation: partial.automation,
    attachments: partial.attachments ?? [],
    createdAt: partial.createdAt ?? now,
    updatedAt: partial.updatedAt ?? now,
  };
}

/** Normalize inbound step arrays for API persistence and responses. */
export function normalizeStructuredSteps(raw: unknown, now = new Date().toISOString()): ScenarioStep[] {
  if (!Array.isArray(raw)) return [];
  const steps: ScenarioStep[] = [];
  for (let i = 0; i < raw.length && steps.length < 50; i += 1) {
    const item = raw[i];
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const action = typeof rec.action === 'string' ? rec.action.trim() : '';
    if (!action) continue;
    const kindRaw = rec.kind ?? rec.keyword;
    const kind =
      kindRaw === 'given' || kindRaw === 'when' || kindRaw === 'then' || kindRaw === 'and' || kindRaw === 'but'
        ? kindRaw
        : undefined;
    const testData =
      typeof rec.testData === 'string'
        ? rec.testData.trim()
        : typeof rec.inputData === 'string'
          ? rec.inputData.trim()
          : '';
    const comment =
      typeof rec.comment === 'string'
        ? rec.comment.trim()
        : typeof rec.comments === 'string'
          ? rec.comments.trim()
          : '';
    const id = typeof rec.id === 'string' && rec.id ? rec.id : undefined;
    const order = typeof rec.order === 'number' && rec.order > 0 ? rec.order : steps.length + 1;
    const expectedResult = typeof rec.expectedResult === 'string' ? rec.expectedResult.trim() : '';
    const actualResult =
      rec.actualResult === null ? null : typeof rec.actualResult === 'string' ? rec.actualResult.trim() || null : null;
    const resultRaw = rec.result;
    const result =
      resultRaw === 'running' ||
      resultRaw === 'passed' ||
      resultRaw === 'failed' ||
      resultRaw === 'blocked' ||
      resultRaw === 'skipped' ||
      resultRaw === 'not_run'
        ? resultRaw
        : 'not_run';
    const stepTypeRaw = rec.stepType;
    const stepType = stepTypeRaw === 'automation' || stepTypeRaw === 'manual' ? stepTypeRaw : 'manual';
    const automationFlowId =
      typeof rec.automationFlowId === 'string' && rec.automationFlowId ? rec.automationFlowId : null;
    let automation: ScenarioStepAutomation | undefined;
    if (rec.automation && typeof rec.automation === 'object') {
      const auto = rec.automation as Record<string, unknown>;
      const modeRaw = auto.mode;
      const mode =
        modeRaw === 'manual' || modeRaw === 'automated' || modeRaw === 'not_automated' ? modeRaw : 'not_automated';
      const actionRaw = auto.action;
      const actionType =
        actionRaw === 'navigate' ||
        actionRaw === 'click' ||
        actionRaw === 'fill' ||
        actionRaw === 'select' ||
        actionRaw === 'upload' ||
        actionRaw === 'hover' ||
        actionRaw === 'wait' ||
        actionRaw === 'assert' ||
        actionRaw === 'api_request'
          ? actionRaw
          : undefined;
      automation = {
        mode,
        action: actionType,
        locator: typeof auto.locator === 'string' ? auto.locator : '',
        timeoutMs: typeof auto.timeoutMs === 'number' ? auto.timeoutMs : undefined,
        retry: typeof auto.retry === 'number' ? auto.retry : undefined,
        screenshot: auto.screenshot === true,
        continueOnFailure: auto.continueOnFailure === true,
        stepType: typeof auto.stepType === 'string' ? auto.stepType : '',
      };
    }
    let attachments: ScenarioEvidence[] = [];
    if (Array.isArray(rec.attachments)) {
      for (const entry of rec.attachments) {
        const parsed = ScenarioEvidenceSchema.safeParse(entry);
        if (parsed.success) {
          attachments.push(parsed.data);
          if (attachments.length >= MAX_SCENARIO_ATTACHMENTS) break;
        }
      }
    }
    steps.push(
      finalizeScenarioStep(
        {
          id,
          order,
          kind,
          action,
          expectedResult,
          inputData: testData,
          testData,
          actualResult,
          result,
          stepType,
          comments: comment,
          comment,
          automationFlowId,
          automation,
          attachments,
          createdAt: typeof rec.createdAt === 'string' ? rec.createdAt : undefined,
          updatedAt: typeof rec.updatedAt === 'string' ? rec.updatedAt : undefined,
        },
        order,
        now,
      ),
    );
  }
  return steps.map((step, index) => ({ ...step, order: index + 1 }));
}

export function finalizeScenarioComponentReference(
  partial: Partial<ScenarioComponentReference> & Pick<ScenarioComponentReference, 'componentId' | 'componentKey'>,
  order: number,
  now = new Date().toISOString(),
): ScenarioComponentReference {
  const parameters =
    partial.parameters && typeof partial.parameters === 'object'
      ? Object.fromEntries(
          Object.entries(partial.parameters)
            .filter(([key]) => key.trim())
            .map(([key, value]) => [key.trim(), typeof value === 'string' ? value : '']),
        )
      : {};
  return {
    stepModel: 'component_reference',
    id: partial.id ?? createStructuredStepId(),
    order,
    componentId: partial.componentId,
    componentKey: partial.componentKey.trim(),
    componentName: (partial.componentName ?? '').trim(),
    parameters,
    createdAt: partial.createdAt ?? now,
    updatedAt: partial.updatedAt ?? now,
  };
}

/** Normalize scenario sequence items (normal steps + component references). */
export function normalizeScenarioSequence(raw: unknown, now = new Date().toISOString()): ScenarioSequenceItem[] {
  if (!Array.isArray(raw)) return [];
  const items: ScenarioSequenceItem[] = [];
  for (let i = 0; i < raw.length && items.length < 50; i += 1) {
    const item = raw[i];
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const order = typeof rec.order === 'number' && rec.order > 0 ? rec.order : items.length + 1;
    if (rec.stepModel === 'component_reference') {
      const componentId = typeof rec.componentId === 'string' ? rec.componentId : '';
      const componentKey = typeof rec.componentKey === 'string' ? rec.componentKey.trim() : '';
      if (!componentId || !componentKey) continue;
      items.push(
        finalizeScenarioComponentReference(
          {
            id: typeof rec.id === 'string' ? rec.id : undefined,
            order,
            componentId,
            componentKey,
            componentName: typeof rec.componentName === 'string' ? rec.componentName : '',
            parameters:
              rec.parameters && typeof rec.parameters === 'object' ? (rec.parameters as Record<string, string>) : {},
            createdAt: typeof rec.createdAt === 'string' ? rec.createdAt : undefined,
            updatedAt: typeof rec.updatedAt === 'string' ? rec.updatedAt : undefined,
          },
          order,
          now,
        ),
      );
      continue;
    }
    const normalized = normalizeStructuredSteps([item], now);
    if (normalized[0]) items.push(normalized[0]);
  }
  return items.map((entry, index) => ({ ...entry, order: index + 1 }));
}

const StructureMetaSchema = z.object({
  description: z.string().default(''),
  tags: z.array(z.string()).default([]),
  ownerUserId: z.string().uuid().nullable().default(null),
  archivedAt: z.string().nullable().default(null),
});

export const QualityDomainSchema = StructureMetaSchema.extend({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
});
export type QualityDomain = z.infer<typeof QualityDomainSchema>;

export const QualityModuleSchema = StructureMetaSchema.extend({
  id: z.string().uuid(),
  domainId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
});
export type QualityModule = z.infer<typeof QualityModuleSchema>;

export const QualityBusinessFlowSchema = StructureMetaSchema.extend({
  id: z.string().uuid(),
  moduleId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  criticality: z.enum(QUALITY_RISK_SEVERITIES).nullable().default(null),
});
export type QualityBusinessFlow = z.infer<typeof QualityBusinessFlowSchema>;

export const ScenarioTreeResponseSchema = z.object({
  projectId: z.string().uuid(),
  domains: z.array(
    QualityDomainSchema.extend({
      modules: z.array(
        QualityModuleSchema.extend({
          flows: z.array(QualityBusinessFlowSchema),
        }),
      ),
    }),
  ),
  requirements: z.array(z.string()),
});
export type ScenarioTreeResponse = z.infer<typeof ScenarioTreeResponseSchema>;

export const CreateStructureNodeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2_000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  criticality: z.enum(QUALITY_RISK_SEVERITIES).nullable().optional(),
  domainId: z.string().uuid().optional(),
  moduleId: z.string().uuid().optional(),
});
export type CreateStructureNodeRequest = z.infer<typeof CreateStructureNodeRequestSchema>;

export const PatchStructureNodeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().max(2_000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  criticality: z.enum(QUALITY_RISK_SEVERITIES).nullable().optional(),
  archived: z.boolean().optional(),
});
export type PatchStructureNodeRequest = z.infer<typeof PatchStructureNodeRequestSchema>;

export const SCENARIO_LINKED_RUN_STATUS = ['running', 'passed', 'failed', 'cancelled'] as const;
export type ScenarioLinkedRunStatus = (typeof SCENARIO_LINKED_RUN_STATUS)[number];

export const ScenarioLinkedFlowSchema = z.object({
  flowId: z.string().uuid(),
  name: z.string(),
  lastRunStatus: z.enum(SCENARIO_LINKED_RUN_STATUS).nullable(),
  lastRunAt: z.string().nullable(),
  /** Null until the Flow has been published at least once (convert/link drafts are not published). */
  publishedAt: z.string().nullable().optional(),
  publishedVersion: z.number().int().optional(),
});
export type ScenarioLinkedFlow = z.infer<typeof ScenarioLinkedFlowSchema>;

export const LinkScenarioFlowRequestSchema = z.object({
  flowId: z.string().uuid(),
});
export type LinkScenarioFlowRequest = z.infer<typeof LinkScenarioFlowRequestSchema>;

export const ScenarioAutomationCandidateSchema = ScenarioLinkedFlowSchema.extend({
  linked: z.boolean(),
});
export type ScenarioAutomationCandidate = z.infer<typeof ScenarioAutomationCandidateSchema>;

export const ScenarioAutomationResponseSchema = z.object({
  linked: z.array(ScenarioLinkedFlowSchema),
  candidates: z.array(ScenarioAutomationCandidateSchema),
});
export type ScenarioAutomationResponse = z.infer<typeof ScenarioAutomationResponseSchema>;

export const SCENARIO_REVIEW_STATES = ['pending', 'approved', 'changes_requested', 'superseded'] as const;
export type ScenarioReviewState = (typeof SCENARIO_REVIEW_STATES)[number];

export const ScenarioReviewDecisionSchema = z.enum(['approved', 'changes_requested']);
export type ScenarioReviewDecision = z.infer<typeof ScenarioReviewDecisionSchema>;

/** One reviewer vote on a Scenario review (migration 207). */
export const ScenarioReviewDecisionRowSchema = z.object({
  reviewerId: z.string().uuid(),
  reviewerName: z.string(),
  decision: ScenarioReviewDecisionSchema,
  comment: z.string().nullable(),
  decidedAt: z.string(),
});
export type ScenarioReviewDecisionRow = z.infer<typeof ScenarioReviewDecisionRowSchema>;

export const ScenarioReviewSchema = z.object({
  id: z.string().uuid(),
  scenarioId: z.string().uuid(),
  requestedBy: z.string().uuid(),
  requestedByName: z.string(),
  state: z.enum(SCENARIO_REVIEW_STATES),
  comment: z.string(),
  snapshotVersion: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  decidedBy: z.string().uuid().nullable().optional(),
  decidedByName: z.string().nullable().optional(),
  decidedAt: z.string().nullable().optional(),
  fromStatus: z.enum(SCENARIO_STATUS).nullable().optional(),
  toStatus: z.enum(SCENARIO_STATUS).nullable().optional(),
  /** Phase 5B.3 — derived from quality_scenario_review_decisions. */
  approvalsCount: z.number().int().nonnegative().default(0),
  changesRequestedCount: z.number().int().nonnegative().default(0),
  /** Effective threshold for UI "X of Y" (required list size or min_approvers). */
  requiredCount: z.number().int().positive().default(1),
  minApprovers: z.number().int().positive().default(1),
  decisions: z.array(ScenarioReviewDecisionRowSchema).default([]),
});
export type ScenarioReview = z.infer<typeof ScenarioReviewSchema>;

/**
 * Phase 8.5.5 — compact AI evaluation snapshot (write-once on accept).
 * Inlined to avoid contracts cycle with scenario-evaluation.ts.
 * Canonical builder: toAcceptanceEvaluationSnapshot in scenario-evaluation.
 */
export const ScenarioAcceptanceEvaluationSnapshotSchema = z.object({
  evaluatorVersion: z.string().trim().min(1).max(40),
  overall: z.number().min(0).max(1),
  structureScore: z.number().min(0).max(1),
  variantScore: z.number().min(0).max(1),
  traceabilityScore: z.number().min(0).max(1),
  groundingScore: z.number().min(0).max(1),
  hygieneScore: z.number().min(0).max(1),
  findingsSummary: z.object({
    highestSeverity: z.enum(['info', 'warn', 'block']),
    info: z.number().int().min(0),
    warn: z.number().int().min(0),
    block: z.number().int().min(0),
    codes: z.array(z.string().trim().min(1).max(80)).max(30),
  }),
  groundingSummary: z.object({
    requirementCovered: z.boolean(),
    acceptanceCriteriaCovered: z.number().int().min(0),
    acceptanceCriteriaTotal: z.number().int().min(0),
    unsupportedClaims: z.number().int().min(0),
  }),
  convertReadyHint: z.boolean(),
  acceptedAt: z.string().min(1).max(40),
});
export type ScenarioAcceptanceEvaluationSnapshot = z.infer<typeof ScenarioAcceptanceEvaluationSnapshotSchema>;

export const QualityScenarioSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  domainId: z.string().uuid().nullable(),
  domain: z.string(),
  moduleId: z.string().uuid().nullable(),
  module: z.string(),
  businessFlowId: z.string().uuid().nullable(),
  businessFlow: z.string(),
  requirementId: z.string().nullable(),
  requirements: ScenarioRequirementsSchema.default({
    userStory: '',
    requirement: '',
    acceptanceCriteria: '',
  }),
  environment: z.enum(SCENARIO_ENVIRONMENTS).nullable().default(null),
  ownerName: z.string().nullable().default(null),
  risk: z.enum(QUALITY_RISK_SEVERITIES).nullable(),
  priority: z.enum(SCENARIO_PRIORITY),
  status: z.enum(SCENARIO_STATUS),
  given: z.string(),
  when: z.string(),
  then: z.string(),
  preconditions: z.array(z.string()),
  steps: z.array(ScenarioSequenceItemSchema),
  testData: z.string(),
  tags: z.array(z.string()),
  coverage: ScenarioCoverageSchema,
  automationStatus: z.enum(SCENARIO_AUTOMATION_STATUS),
  healthStatus: z.enum(SCENARIO_HEALTH),
  needsReviewReason: z.string().default(''),
  lastExecutionStatus: z.enum(SCENARIO_EXECUTION_STATUS).nullable(),
  lastExecutionId: z.string().uuid().nullable(),
  lastExecutedAt: z.string().nullable(),
  passRate: z.number().min(0).max(100).nullable().optional(),
  executionCount: z.number().int().nonnegative().optional(),
  version: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByName: z.string().nullable(),
  updatedByName: z.string().nullable(),
  assignedToName: z.string().nullable(),
  assignedToUserId: z.string().uuid().nullable().optional(),
  attachments: z.array(ScenarioEvidenceSchema).max(MAX_SCENARIO_ATTACHMENTS).default([]),
  linkedFlows: z.array(ScenarioLinkedFlowSchema).default([]),
  linkedRequirements: z.array(LinkedRequirementSchema).default([]),
  sourceStoryLinkId: z.string().uuid().nullable().optional(),
  review: ScenarioReviewSchema.nullable().optional(),
  /**
   * Phase 8.5.5 — historical AI evaluation snapshot at accept time.
   * Write-once; null/omitted for manual creates. Never rewritten by later eval.
   */
  aiAcceptanceEvaluation: ScenarioAcceptanceEvaluationSnapshotSchema.nullable().optional(),
});
export type QualityScenario = z.infer<typeof QualityScenarioSchema>;

export const ScenarioPassRateTrendDaySchema = z.object({
  date: z.string(),
  passRate: z.number().min(0).max(100),
  executionCount: z.number().int().nonnegative(),
});
export type ScenarioPassRateTrendDay = z.infer<typeof ScenarioPassRateTrendDaySchema>;

export const ScenarioListResponseSchema = z.object({
  projectId: z.string().uuid(),
  scenarios: z.array(QualityScenarioSchema),
  passRateTrend: z.array(ScenarioPassRateTrendDaySchema).optional(),
});
export type ScenarioListResponse = z.infer<typeof ScenarioListResponseSchema>;

export const ScenarioResponseSchema = z.object({
  scenario: QualityScenarioSchema,
});
export type ScenarioResponse = z.infer<typeof ScenarioResponseSchema>;

export const AutomateScenarioResponseSchema = z.object({
  scenario: QualityScenarioSchema,
  flowId: z.string().uuid(),
  flowName: z.string(),
});
export type AutomateScenarioResponse = z.infer<typeof AutomateScenarioResponseSchema>;

export const CreateScenarioRequestSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().max(2_000).optional(),
  businessFlowId: z.string().uuid(),
  requirementId: z.string().max(80).nullable().optional(),
  requirements: ScenarioRequirementsSchema.optional(),
  environment: z.enum(SCENARIO_ENVIRONMENTS).nullable().optional(),
  ownerUserId: z.string().uuid().nullable().optional(),
  risk: z.enum(QUALITY_RISK_SEVERITIES).nullable().optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
  status: z.enum(SCENARIO_STATUS).optional(),
  given: z.string().max(2_000).optional(),
  when: z.string().max(2_000).optional(),
  then: z.string().max(2_000).optional(),
  preconditions: z.array(z.string().max(300)).max(20).optional(),
  steps: z.array(ScenarioSequenceInputSchema).max(50).optional(),
  testData: z.string().max(8_000).optional(),
  tags: z.array(z.string().max(40)).max(20).optional(),
  attachments: z.array(ScenarioEvidenceSchema).max(MAX_SCENARIO_ATTACHMENTS).optional(),
  automationIntent: z.enum(SCENARIO_AUTOMATION_INTENT).optional(),
  requirementIds: z.array(z.string().uuid()).max(20).optional(),
  /**
   * Phase 8.5.5 — optional compact evaluation snapshot when accepting an AI proposal.
   * Advisory only; not required for create/approval. Persisted write-once.
   */
  aiAcceptanceEvaluation: ScenarioAcceptanceEvaluationSnapshotSchema.optional(),
});
export type CreateScenarioRequest = z.infer<typeof CreateScenarioRequestSchema>;

export const PatchScenarioRequestSchema = CreateScenarioRequestSchema.partial().extend({
  title: z.string().trim().min(1).max(200).optional(),
  businessFlowId: z.string().uuid().optional(),
  assignedToUserId: z.union([z.string().uuid(), z.null()]).optional(),
});
export type PatchScenarioRequest = z.infer<typeof PatchScenarioRequestSchema>;

/** Metadata-only bulk patch. Never includes steps or Given/When/Then. */
export const BULK_SCENARIO_PATCH_MAX = 100;

export const BulkPatchScenarioFieldsSchema = z
  .object({
    risk: z.enum(QUALITY_RISK_SEVERITIES).nullable().optional(),
    priority: z.enum(SCENARIO_PRIORITY).optional(),
    status: z.enum(SCENARIO_STATUS).optional(),
    healthStatus: z.enum(SCENARIO_HEALTH).optional(),
    businessFlowId: z.string().uuid().optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    tagsMode: z.enum(['replace', 'append']).optional(),
  })
  .refine((value) => Object.keys(value).some((key) => key !== 'tagsMode'), {
    message: 'At least one field is required',
  });
export type BulkPatchScenarioFields = z.infer<typeof BulkPatchScenarioFieldsSchema>;

export const BulkPatchScenariosRequestSchema = z.object({
  scenarioIds: z.array(z.string().uuid()).min(1).max(BULK_SCENARIO_PATCH_MAX),
  patch: BulkPatchScenarioFieldsSchema,
  confirmHealthOverride: z.boolean().optional(),
});
export type BulkPatchScenariosRequest = z.infer<typeof BulkPatchScenariosRequestSchema>;

export const BulkPatchScenariosResponseSchema = z.object({
  updated: z.number().int().positive(),
  scenarios: z.array(QualityScenarioSchema),
});
export type BulkPatchScenariosResponse = z.infer<typeof BulkPatchScenariosResponseSchema>;

export const BULK_SCENARIO_MARK_MAX = 50;

export const BulkMarkScenarioResultsRequestSchema = z
  .object({
    scenarioIds: z.array(z.string().uuid()).min(1).max(BULK_SCENARIO_MARK_MAX),
    status: z.enum(['passed', 'failed', 'blocked', 'skipped']),
    comment: z.string().max(4_000).optional(),
  })
  .superRefine((value, ctx) => {
    if ((value.status === 'failed' || value.status === 'blocked') && !(value.comment || '').trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Comment is required', path: ['comment'] });
    }
  });
export type BulkMarkScenarioResultsRequest = z.infer<typeof BulkMarkScenarioResultsRequestSchema>;

export const BulkMarkScenarioResultsResponseSchema = z.object({
  marked: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type BulkMarkScenarioResultsResponse = z.infer<typeof BulkMarkScenarioResultsResponseSchema>;

export const CHANGE_IMPACT_STALE_DAYS = 14;
export const CHANGE_IMPACT_REASONS = [
  'open_failure',
  'same_flow',
  'same_module',
  'last_run_failed',
  'stale',
  'code_change',
] as const;
export type ChangeImpactReason = (typeof CHANGE_IMPACT_REASONS)[number];

export const ChangeImpactScenarioSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  domain: z.string(),
  module: z.string(),
  businessFlow: z.string(),
  lastExecutionStatus: z.enum(['passed', 'failed', 'blocked', 'skipped']).nullable(),
  lastExecutedAt: z.string().nullable(),
  openFailureKey: z.string().nullable(),
  needsReviewReason: z.string().default(''),
  reasons: z.array(z.enum(CHANGE_IMPACT_REASONS)).min(1),
  hasAutomation: z.boolean().default(false),
  maintenanceKind: z.enum(['manual', 'both']).optional(),
  requirementKeys: z.array(z.string()).max(20).optional(),
  requirementIds: z.array(z.string().uuid()).max(20).optional(),
});
export type ChangeImpactScenario = z.infer<typeof ChangeImpactScenarioSchema>;

export const ChangeReviewUpdateSchema = z.object({
  scenarioId: z.string().uuid(),
  title: z.string(),
  reason: z.string(),
  expected: z.string().default(''),
  actual: z.string().default(''),
});
export type ChangeReviewUpdate = z.infer<typeof ChangeReviewUpdateSchema>;

export const ChangeReviewAddSchema = z.object({
  title: z.string(),
  reason: z.string(),
  businessFlowId: z.string().uuid().nullable(),
  given: z.string().default(''),
  when: z.string().default(''),
  then: z.string().default(''),
  createdScenarioId: z.string().uuid().nullable().optional(),
});
export type ChangeReviewAdd = z.infer<typeof ChangeReviewAddSchema>;

export const ChangeReviewRunSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  /** TIA match score when known (0..1). Optional for older rows. */
  score: z.number().min(0).max(1).optional(),
  matchedRoutes: z.array(z.string()).optional(),
});
export type ChangeReviewRun = z.infer<typeof ChangeReviewRunSchema>;

export const ChangeReviewSuggestionsSchema = z.object({
  update: z.array(ChangeReviewUpdateSchema).default([]),
  add: z.array(ChangeReviewAddSchema).default([]),
  run: z.array(ChangeReviewRunSchema).default([]),
});
export type ChangeReviewSuggestions = z.infer<typeof ChangeReviewSuggestionsSchema>;

/** Canonical impact severity — one model for Quality-first and Automation-first. */
export const IMPACT_LEVELS = ['none', 'low', 'medium', 'high', 'critical'] as const;
export type ImpactLevel = (typeof IMPACT_LEVELS)[number];

export const IMPACT_CONFIDENCE = ['low', 'medium', 'high'] as const;
export type ImpactConfidence = (typeof IMPACT_CONFIDENCE)[number];

export const CHANGE_REVIEW_STATUSES = ['open', 'needs_review', 'reviewed', 'dismissed'] as const;
export type ChangeReviewStatus = (typeof CHANGE_REVIEW_STATUSES)[number];

/** #15 — what needs attention (advisory). Distinct from reviewStatus open/reviewed/dismissed. */
export const MAINTENANCE_RECOMMENDATIONS = [
  'none',
  'manual_review',
  'automation_review',
  'manual_and_automation_review',
  'retest',
  'no_change',
  'dismissed',
] as const;
export type MaintenanceRecommendation = (typeof MAINTENANCE_RECOMMENDATIONS)[number];

/** #15 — explicit human decisions. Never auto-applied as silent rewrites. */
export const MAINTENANCE_DECISIONS = [
  'no_change_required',
  'manual_update_required',
  'automation_update_required',
  'both_update_required',
  'retest_required',
  'dismiss',
] as const;
export type MaintenanceDecision = (typeof MAINTENANCE_DECISIONS)[number];

export const MAINTENANCE_TARGET_KINDS = ['manual', 'automation', 'both', 'automation_first'] as const;
export type MaintenanceTargetKind = (typeof MAINTENANCE_TARGET_KINDS)[number];

export const MaintenanceTargetSchema = z.object({
  kind: z.enum(MAINTENANCE_TARGET_KINDS),
  scenarioId: z.string().uuid().optional(),
  scenarioTitle: z.string().optional(),
  flowId: z.string().uuid().optional(),
  flowName: z.string().optional(),
  requirementIds: z.array(z.string().uuid()).max(20).optional(),
  requirementKeys: z.array(z.string()).max(20).optional(),
  impactLevel: z.enum(IMPACT_LEVELS).optional(),
  confidence: z.enum(IMPACT_CONFIDENCE).optional(),
  reason: z.string().max(500),
  recommendation: z.enum(MAINTENANCE_RECOMMENDATIONS),
  evidence: z.array(z.string().max(300)).max(20).default([]),
});
export type MaintenanceTarget = z.infer<typeof MaintenanceTargetSchema>;

export const MaintenanceDecisionRecordSchema = z.object({
  decision: z.enum(MAINTENANCE_DECISIONS),
  decidedAt: z.string(),
  decidedBy: z.string().uuid().nullable().optional(),
  note: z.string().max(2_000).optional(),
  retestRunIds: z.array(z.string().uuid()).max(50).optional(),
});
export type MaintenanceDecisionRecord = z.infer<typeof MaintenanceDecisionRecordSchema>;

/** Stored inside findings JSONB — no second impact/maintenance table. */
export const ChangeImpactMaintenanceSchema = z.object({
  recommendation: z.enum(MAINTENANCE_RECOMMENDATIONS).default('none'),
  reason: z.string().max(2_000).default(''),
  confidence: z.enum(IMPACT_CONFIDENCE).default('low'),
  manualCount: z.number().int().nonnegative().default(0),
  automationCount: z.number().int().nonnegative().default(0),
  bothCount: z.number().int().nonnegative().default(0),
  automationOnlyCount: z.number().int().nonnegative().default(0),
  targets: z.array(MaintenanceTargetSchema).max(200).default([]),
  decision: MaintenanceDecisionRecordSchema.nullable().optional(),
  history: z.array(MaintenanceDecisionRecordSchema).max(50).default([]),
});
export type ChangeImpactMaintenance = z.infer<typeof ChangeImpactMaintenanceSchema>;

/** #16 — developer packaging on the same findings JSONB (no second impact store). */
export const DeveloperReviewerSchema = z.object({
  userId: z.string().uuid(),
  role: z.enum(['scenario_assignee', 'scenario_owner', 'scenario_creator', 'flow_owner', 'project_member']),
  label: z.string().max(200),
});
export type DeveloperReviewer = z.infer<typeof DeveloperReviewerSchema>;

export const DeveloperIntelSchema = z.object({
  commitSha: z.string().max(64),
  prNumber: z.number().int().positive().nullable().optional(),
  provider: z.enum(['github', 'gitlab', 'bitbucket', 'azure_devops']).nullable().optional(),
  source: z.enum(['push', 'pull_request', 'compare', 'tia', 'manual']).optional(),
  impactLevel: z.enum(IMPACT_LEVELS),
  confidence: z.enum(IMPACT_CONFIDENCE),
  reviewStatus: z.enum(CHANGE_REVIEW_STATUSES),
  needsReview: z.boolean(),
  affectedAutomation: z
    .array(z.object({ id: z.string().uuid(), name: z.string(), automationFirst: z.boolean().optional() }))
    .max(100)
    .default([]),
  affectedManual: z
    .array(z.object({ id: z.string().uuid(), name: z.string() }))
    .max(100)
    .default([]),
  affectedRequirements: z
    .array(z.object({ id: z.string().uuid(), key: z.string(), name: z.string().optional() }))
    .max(100)
    .default([]),
  maintenanceRequired: z
    .object({
      manual: z.number().int().nonnegative(),
      automation: z.number().int().nonnegative(),
      both: z.number().int().nonnegative(),
      recommendation: z.string().max(80),
    })
    .optional(),
  coverageGaps: z
    .array(z.object({ kind: z.string().max(80), label: z.string().max(300), detail: z.string().max(500) }))
    .max(50)
    .default([]),
  recommendedActions: z.array(z.string().max(300)).max(20).default([]),
  reviewers: z.array(DeveloperReviewerSchema).max(50).default([]),
  /** Composed from #14 when available — never invents %. */
  risk: z
    .object({
      riskLevel: z.enum(['low', 'medium', 'high', 'critical']),
      riskScore: z.number().min(0).max(100),
      confidence: z.enum(['low', 'medium', 'high']),
      requiredCount: z.number().int().nonnegative().optional(),
      recommendedCount: z.number().int().nonnegative().optional(),
    })
    .nullable()
    .optional(),
  /** Explicit locks — #16 never auto-rewrites or auto-executes. */
  rewritten: z.literal(false).default(false),
  executed: z.literal(false).default(false),
});
export type DeveloperIntel = z.infer<typeof DeveloperIntelSchema>;

/** Feature 1E — pipeline apply health stamped on the change review. */
export const ChangeImpactApplyPipelineSchema = z.object({
  ok: z.boolean(),
  code: z.literal('CHANGE_IMPACT_APPLY_FAILED').optional(),
  message: z.string().max(500).optional(),
  at: z.string().optional(),
});
export type ChangeImpactApplyPipeline = z.infer<typeof ChangeImpactApplyPipelineSchema>;

/**
 * Feature 1C — analysis availability for automation impact (empty graph ≠ no impact).
 * Declared early so 4B selection metrics / findings can reuse it.
 */
export const CHANGE_IMPACT_ANALYSIS_STATUS = ['ready', 'limited', 'unavailable'] as const;
export type ChangeImpactAnalysisStatus = (typeof CHANGE_IMPACT_ANALYSIS_STATUS)[number];

/**
 * Feature 4B — selected vs suite metrics (compose-on-read).
 * Counts only — never invents “tests saved”, “time saved”, or safe-skip (4D).
 */
export const SELECTION_METRICS_STATUS = ['available', 'limited', 'insufficient_evidence'] as const;
export type SelectionMetricsStatus = (typeof SELECTION_METRICS_STATUS)[number];

export const ChangeImpactSelectionMetricsSchema = z.object({
  attribution: z.literal('selection_metrics'),
  status: z.enum(SELECTION_METRICS_STATUS),
  /** Flows in this Change Review / TIA suggestion set. */
  selectedFlowCount: z.number().int().nonnegative(),
  /** Project automation suite size (non-archived flows). */
  suiteFlowCount: z.number().int().nonnegative(),
  /**
   * suite − selected (floor 0). Math only — label as “not in this selection”,
   * never “safely skipped” or “tests saved”.
   */
  notSelectedCount: z.number().int().nonnegative(),
  /** selected / suite when suite > 0; otherwise null. */
  selectionRatio: z.number().min(0).max(1).nullable(),
  /** Impacted automation rows on the Change Impact surface (may exceed / differ from run suggestions). */
  impactedFlowCount: z.number().int().nonnegative(),
  selectedAndImpactedCount: z.number().int().nonnegative(),
  impactedNotInSelectionCount: z.number().int().nonnegative(),
  selectedNotInImpactCount: z.number().int().nonnegative(),
  /** Mirror of review.runsStarted when a review exists. */
  runsStarted: z.boolean().nullable(),
  /**
   * Selected flows with a test_run stamped for this commit SHA.
   * Null when commit-linked execution evidence is unavailable.
   */
  executedSelectedCount: z.number().int().nonnegative().nullable(),
  executedSelectedKnown: z.boolean(),
  /**
   * Sum of last-known durations (ms) for selected flows — historical volume estimate only.
   * Not “suite minutes avoided”.
   */
  historicalDurationMsSelected: z.number().int().nonnegative().nullable(),
  historicalDurationKnown: z.boolean(),
  analysisStatus: z.enum(CHANGE_IMPACT_ANALYSIS_STATUS).nullable(),
  limitations: z.array(z.string().max(300)).max(12).default([]),
  evidenceNotes: z.array(z.string().max(300)).max(12).default([]),
  /** Explicit product locks — Wave 4B never claims these. */
  claimsTestsSaved: z.literal(false),
  claimsSafeSkip: z.literal(false),
  claimsTimeSaved: z.literal(false),
});
export type ChangeImpactSelectionMetrics = z.infer<typeof ChangeImpactSelectionMetricsSchema>;

export const ChangeImpactFindingsSchema = z.object({
  maxScore: z.number().min(0).max(1).default(0),
  scenarioCount: z.number().int().nonnegative().default(0),
  flowCount: z.number().int().nonnegative().default(0),
  componentCount: z.number().int().nonnegative().default(0),
  requirementCount: z.number().int().nonnegative().default(0),
  reasons: z.array(z.string()).default([]),
  recommendedActions: z.array(z.string()).default([]),
  /** #15 maintenance orchestration — same quality_change_reviews row. */
  maintenance: ChangeImpactMaintenanceSchema.optional(),
  /** #16 developer packaging — same quality_change_reviews row. */
  developer: DeveloperIntelSchema.optional(),
  /** #17 production packaging — same quality_change_reviews row. */
  production: ProductionIntelSchema.optional(),
  /** Feature 1E — last pipeline apply outcome for this review (advisory). */
  applyPipeline: ChangeImpactApplyPipelineSchema.optional(),
  /**
   * Feature 4B — snapshot of selected vs suite counts at apply time (Wave 6 ROI handoff).
   * Compose-on-read on Change Impact remains authoritative for live UI.
   */
  selectionMetrics: ChangeImpactSelectionMetricsSchema.optional(),
});
export type ChangeImpactFindings = z.infer<typeof ChangeImpactFindingsSchema>;

export const ChangeReviewSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  commitSha: z.string(),
  message: z.string(),
  linkedFailureKey: z.string(),
  files: z.array(z.string()),
  suggestions: ChangeReviewSuggestionsSchema,
  updatesAccepted: z.boolean(),
  draftsCreated: z.boolean(),
  runsStarted: z.boolean(),
  reviewStatus: z.enum(CHANGE_REVIEW_STATUSES).default('open'),
  impactLevel: z.enum(IMPACT_LEVELS).default('none'),
  confidence: z.enum(IMPACT_CONFIDENCE).default('low'),
  findings: ChangeImpactFindingsSchema.default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChangeReview = z.infer<typeof ChangeReviewSchema>;

/** Automation-first rows on the same Change Impact surface (no Scenario required). */
export const ChangeImpactFlowSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  score: z.number().min(0).max(1).optional(),
  matchedRoutes: z.array(z.string()).optional(),
  reviewRequired: z.boolean().default(true),
  /** True when no Quality Scenario maps to this flow. */
  automationFirst: z.boolean().default(false),
  /**
   * Feature 1C — why this flow was selected (deterministic chips).
   * Never claims the commit broke the flow.
   */
  impactLevel: z.enum(IMPACT_LEVELS).optional(),
  selectionKind: z.enum(['tia', 'call_flow_parent', 'linked_failure', 'scenario_link', 'unknown']).optional(),
  why: z.array(z.string().max(200)).max(12).optional(),
  matchedFiles: z.array(z.string().max(500)).max(20).optional(),
  /** Capability #5 — selectors/locators matched from change tokens (when available). */
  matchedSelectors: z.array(z.string().max(300)).max(12).optional(),
  relatedScenarioTitles: z.array(z.string().max(300)).max(8).optional(),
  relatedRequirementKeys: z.array(z.string().max(80)).max(8).optional(),
  historicalFailureCount: z.number().int().nonnegative().optional(),
  maintenanceHint: z.string().max(300).nullable().optional(),
});
export type ChangeImpactFlow = z.infer<typeof ChangeImpactFlowSchema>;

/**
 * Feature 1C — analysis availability for automation impact (empty graph ≠ no impact).
 * Canonical definition lives above ChangeImpactFindingsSchema (shared with 4B).
 */
export const CHANGE_IMPACT_PREREQ_STATUS = ['ready', 'missing', 'empty', 'unknown'] as const;
export type ChangeImpactPrereqStatus = (typeof CHANGE_IMPACT_PREREQ_STATUS)[number];

/** Feature 0B/0C — prerequisite health for TIA / Change Impact honesty. */
export const ChangeImpactTiaPrerequisitesSchema = z.object({
  repositoryBound: z.boolean(),
  repositoryCount: z.number().int().nonnegative(),
  repositoryStatus: z.enum(CHANGE_IMPACT_PREREQ_STATUS),
  routeMapReady: z.boolean(),
  routeMapEntryCount: z.number().int().nonnegative(),
  routeMapStatus: z.enum(CHANGE_IMPACT_PREREQ_STATUS),
  flowGraphReady: z.boolean(),
  flowGraphRowCount: z.number().int().nonnegative(),
  flowGraphStatus: z.enum(CHANGE_IMPACT_PREREQ_STATUS),
  /** True when a tia_commit_index row exists for the reviewed commit (if any). */
  tiaIndexed: z.boolean(),
  tiaStatus: z.enum(CHANGE_IMPACT_PREREQ_STATUS),
});
export type ChangeImpactTiaPrerequisites = z.infer<typeof ChangeImpactTiaPrerequisitesSchema>;

export const ChangeImpactSetupActionSchema = z.object({
  code: z.enum(['bind_repository', 'configure_route_map', 'populate_flow_graph', 'run_tia']),
  message: z.string().max(300),
  href: z.string().max(500).optional(),
});
export type ChangeImpactSetupAction = z.infer<typeof ChangeImpactSetupActionSchema>;

export const ChangeImpactAutomationImpactSchema = z.object({
  attribution: z.literal('tia_explain'),
  analysisStatus: z.enum(CHANGE_IMPACT_ANALYSIS_STATUS),
  /** Human-readable hints when evidence is thin (not "no impact"). */
  analysisHints: z.array(z.string().max(300)).max(10).default([]),
  flowCount: z.number().int().nonnegative(),
  highImpactCount: z.number().int().nonnegative(),
  unmappedFileCount: z.number().int().nonnegative().default(0),
  routeMapEntryCount: z.number().int().nonnegative().default(0),
  flowGraphRowCount: z.number().int().nonnegative().default(0),
  /** Feature 0B/0C — explicit readiness; empty graph ≠ zero impact. */
  prerequisites: ChangeImpactTiaPrerequisitesSchema.optional(),
  setupActions: z.array(ChangeImpactSetupActionSchema).max(8).default([]),
  /** False when automation impact cannot be reliably determined. */
  canDetermineAutomationImpact: z.boolean().default(true),
});
export type ChangeImpactAutomationImpact = z.infer<typeof ChangeImpactAutomationImpactSchema>;

/**
 * Feature 1D — recent contract-check drift on Change Impact (compose-on-read).
 * Attribution is schedule/related — never claims this commit broke the API.
 */
export const CHANGE_IMPACT_CONTRACT_EVIDENCE = ['direct', 'strong', 'possible', 'limited'] as const;
export type ChangeImpactContractEvidence = (typeof CHANGE_IMPACT_CONTRACT_EVIDENCE)[number];

export const ChangeImpactContractFindingSchema = z.object({
  checkId: z.string().uuid(),
  checkName: z.string().max(200),
  runId: z.string().uuid(),
  kind: z.enum(['openapi-compare', 'pact-verify']),
  outcome: z.enum(['failed', 'errored']),
  startedAt: z.string(),
  breakingCount: z.number().int().nonnegative().default(0),
  /** Sample operation keys e.g. `GET /users` from DiffFinding.path. */
  sampleEndpoints: z.array(z.string().max(200)).max(8).default([]),
  /** Soft join: flow_graph routes intersecting finding paths. */
  matchedFlowRoutes: z.array(z.string().max(200)).max(8).default([]),
  matchedFlowIds: z.array(z.string().uuid()).max(12).default([]),
  evidence: z.enum(CHANGE_IMPACT_CONTRACT_EVIDENCE).default('limited'),
  deepLink: z.string().max(500).optional(),
});
export type ChangeImpactContractFinding = z.infer<typeof ChangeImpactContractFindingSchema>;

export const ChangeImpactContractDriftSchema = z.object({
  attribution: z.literal('related_contract_check'),
  windowDays: z.number().int().positive(),
  failedRunCount: z.number().int().nonnegative(),
  breakingFindingCount: z.number().int().nonnegative(),
  checkCount: z.number().int().nonnegative(),
  items: z.array(ChangeImpactContractFindingSchema).max(12),
  contractsDeepLink: z.string().max(500),
  analysisHints: z.array(z.string().max(300)).max(8).default([]),
});
export type ChangeImpactContractDrift = z.infer<typeof ChangeImpactContractDriftSchema>;

/**
 * Wave 5A — visual evidence soft-join on Change Impact (deep links only).
 * Never claims application defect; never auto-blocks release.
 */
export const ChangeImpactVisualEvidenceSchema = z.object({
  attribution: z.literal('related_visual_check'),
  signal: z.literal('VISUAL_CHANGE_DETECTED'),
  band: z.enum(['available', 'limited', 'unavailable', 'none']),
  summary: z.string().max(500),
  why: z.array(z.string().max(300)).max(8).default([]),
  commitSha: z.string().max(64).nullable(),
  runCount: z.number().int().nonnegative(),
  mismatchCount: z.number().int().nonnegative(),
  newBaselineCount: z.number().int().nonnegative(),
  claimsApplicationDefect: z.literal(false),
  autoBlocksRelease: z.literal(false),
  items: z
    .array(
      z.object({
        runId: z.string().uuid(),
        flowId: z.string().uuid(),
        flowName: z.string().max(200),
        stepId: z.string().max(120),
        stepTitle: z.string().max(300),
        status: z.enum(['mismatch', 'new_baseline']),
        matchPercentage: z.number(),
        threshold: z.number(),
        diffsHref: z.string().max(500),
        claimsApplicationDefect: z.literal(false),
      }),
    )
    .max(8)
    .default([]),
  releaseDrilldownHref: z.string().max(500).nullable(),
});
export type ChangeImpactVisualEvidence = z.infer<typeof ChangeImpactVisualEvidenceSchema>;

/**
 * Feature 2A — maintenance recommendation explainability (compose-on-read).
 * Distinct from MAINTENANCE_RECOMMENDATIONS (action kind): this is certainty + why.
 */
export const MAINTENANCE_EXPLAIN_OUTCOMES = [
  'no_maintenance',
  'review_recommended',
  'maintenance_likely',
  'maintenance_confirmed',
  'insufficient_evidence',
] as const;
export type MaintenanceExplainOutcome = (typeof MAINTENANCE_EXPLAIN_OUTCOMES)[number];

export const MAINTENANCE_EVIDENCE_LEANS = ['test', 'product', 'environment', 'data', 'mixed', 'unknown'] as const;
export type MaintenanceEvidenceLean = (typeof MAINTENANCE_EVIDENCE_LEANS)[number];

export const ChangeImpactMaintenanceCategorySignalSchema = z.object({
  category: z.string().max(80),
  count: z.number().int().nonnegative(),
  lean: z.enum(['test', 'product', 'environment', 'data', 'unknown']),
});
export type ChangeImpactMaintenanceCategorySignal = z.infer<typeof ChangeImpactMaintenanceCategorySignalSchema>;

export const ChangeImpactMaintenanceExplainItemSchema = z.object({
  kind: z.enum(MAINTENANCE_TARGET_KINDS),
  title: z.string().max(300),
  flowId: z.string().uuid().optional(),
  scenarioId: z.string().uuid().optional(),
  outcome: z.enum(MAINTENANCE_EXPLAIN_OUTCOMES),
  why: z.array(z.string().max(200)).max(8).default([]),
  suggestedAction: z.string().max(300),
});
export type ChangeImpactMaintenanceExplainItem = z.infer<typeof ChangeImpactMaintenanceExplainItemSchema>;

export const ChangeImpactMaintenanceExplainSchema = z.object({
  attribution: z.literal('maintenance_explain'),
  outcome: z.enum(MAINTENANCE_EXPLAIN_OUTCOMES),
  confidence: z.enum(IMPACT_CONFIDENCE),
  /** Certainty/why chips — never "commit broke the test". */
  why: z.array(z.string().max(300)).max(12).default([]),
  suggestedAction: z.string().max(300),
  /** Mirrors #15 action kind (what work), not certainty. */
  actionKind: z.enum(MAINTENANCE_RECOMMENDATIONS),
  evidenceLean: z.enum(MAINTENANCE_EVIDENCE_LEANS),
  categorySignals: z.array(ChangeImpactMaintenanceCategorySignalSchema).max(12).default([]),
  relatedFailureCount: z.number().int().nonnegative().default(0),
  flakeHypothesisCount: z.number().int().nonnegative().default(0),
  contractDriftSupporting: z.boolean().default(false),
  failuresDeepLink: z.string().max(500).optional(),
  items: z.array(ChangeImpactMaintenanceExplainItemSchema).max(8).default([]),
});
export type ChangeImpactMaintenanceExplain = z.infer<typeof ChangeImpactMaintenanceExplainSchema>;

/**
 * Feature 1A — historical Failure Intelligence on Change Impact.
 * Never claims the reviewed commit caused these failures.
 */
export const CHANGE_IMPACT_EVIDENCE_RELEVANCE = ['impacted_scenario', 'impacted_automation'] as const;
export type ChangeImpactEvidenceRelevance = (typeof CHANGE_IMPACT_EVIDENCE_RELEVANCE)[number];

export const CHANGE_IMPACT_EVIDENCE_CATEGORY_SOURCE = ['failure_type', 'heuristic'] as const;
export type ChangeImpactEvidenceCategorySource = (typeof CHANGE_IMPACT_EVIDENCE_CATEGORY_SOURCE)[number];

export const ChangeImpactEvidenceCategorySchema = z.object({
  /** Stable bucket id — failure_type value, or heuristic label (locator / api / other). */
  category: z.string().max(80),
  count: z.number().int().nonnegative(),
  source: z.enum(CHANGE_IMPACT_EVIDENCE_CATEGORY_SOURCE).default('failure_type'),
});
export type ChangeImpactEvidenceCategory = z.infer<typeof ChangeImpactEvidenceCategorySchema>;

export const ChangeImpactEvidenceItemSchema = z.object({
  failureId: z.string().uuid(),
  displayKey: z.string().max(40),
  scenarioId: z.string().uuid().nullable(),
  scenarioTitle: z.string().max(300),
  status: z.enum(['open', 'linked', 'resolved']),
  failureType: z.string().max(80).nullable(),
  relevance: z.enum(CHANGE_IMPACT_EVIDENCE_RELEVANCE),
  createdAt: z.string(),
  deepLink: z.string().max(500),
  /** Optional automation flow when failure came via execution bridge (1A/1C). */
  automationFlowId: z.string().uuid().nullable().optional(),
});
export type ChangeImpactEvidenceItem = z.infer<typeof ChangeImpactEvidenceItemSchema>;

/**
 * Feature 3B+3C — resolution enrichment on Change Impact historical evidence.
 * Compose-on-read from the same bounded FI fetch; never claims commit causation.
 */
export const CHANGE_IMPACT_RESOLUTION_MATCH = ['none', 'limited', 'possible', 'strong', 'verified'] as const;
export type ChangeImpactResolutionMatch = (typeof CHANGE_IMPACT_RESOLUTION_MATCH)[number];

export const ChangeImpactResolutionSampleSchema = z.object({
  failureId: z.string().uuid(),
  displayKey: z.string().max(40),
  verification: z.enum(['verified', 'unverified']),
  category: z.string().max(40),
  rootCause: z.string().max(300),
  verifiedBy: z.string().max(120).optional(),
  fixedBuild: z.string().max(80).optional(),
  resolvedAt: z.string().nullable(),
  deepLink: z.string().max(500),
});
export type ChangeImpactResolutionSample = z.infer<typeof ChangeImpactResolutionSampleSchema>;

export const ChangeImpactResolutionEnrichmentSchema = z.object({
  matchStrength: z.enum(CHANGE_IMPACT_RESOLUTION_MATCH),
  verifiedCount: z.number().int().nonnegative(),
  unverifiedCount: z.number().int().nonnegative(),
  lastOccurrenceAt: z.string().nullable(),
  topCategory: z.string().max(40).nullable(),
  summary: z.string().max(500),
  why: z.array(z.string().max(300)).max(10),
  samples: z.array(ChangeImpactResolutionSampleSchema).max(5),
  /**
   * Feature 7E — how many sampled resolutions have a closed tracker issue.
   * Explanatory only: it never raises `matchStrength` or the recommendation,
   * because a closed issue is not proof the defect was fixed.
   */
  issueBackedCount: z.number().int().nonnegative().optional(),
  governance: z.object({
    autoApply: z.literal(false),
    silentPublish: z.literal(false),
    recommendationsAreNotTruth: z.literal(true),
  }),
});
export type ChangeImpactResolutionEnrichment = z.infer<typeof ChangeImpactResolutionEnrichmentSchema>;

export const ChangeImpactHistoricalEvidenceSchema = z.object({
  /** Explicit non-causation marker — UI must not say "this commit caused…". */
  attribution: z.literal('related_historical'),
  windowDays: z.number().int().positive(),
  recentDays: z.number().int().positive(),
  relatedFailureCount: z.number().int().nonnegative(),
  recentFailureCount: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  resolvedCount: z.number().int().nonnegative(),
  categories: z.array(ChangeImpactEvidenceCategorySchema).max(20),
  flakeHypothesisCount: z.number().int().nonnegative().default(0),
  /** Bounded sample for the panel — not the full history. */
  items: z.array(ChangeImpactEvidenceItemSchema).max(20),
  failuresDeepLink: z.string().max(500),
  /** Feature 3B+3C — verified/unverified resolution summary from same fetch. */
  resolutionEnrichment: ChangeImpactResolutionEnrichmentSchema.optional(),
});
export type ChangeImpactHistoricalEvidence = z.infer<typeof ChangeImpactHistoricalEvidenceSchema>;

/**
 * Feature 1B — commit/PR ticket → requirement / story linkage on Change Impact.
 * Never claims the commit "caused" or "implements" a requirement without evidence.
 */
export const CHANGE_IMPACT_REQUIREMENT_EVIDENCE = [
  /** Key found in stored commit / push message. */
  'commit_message',
  /** Key found in PR/MR title path (developer.source === pull_request). */
  'pr_title',
  /** Requirement already linked to an impacted scenario (existing Rynora graph). */
  'impacted_scenario',
  /** Matched `story_links.source_ref` for this project. */
  'story_link',
  /** Ticket-shaped key in message but no project requirement/story row. */
  'unmatched_key',
] as const;
export type ChangeImpactRequirementEvidence = (typeof CHANGE_IMPACT_REQUIREMENT_EVIDENCE)[number];

export const CHANGE_IMPACT_REQUIREMENT_CONFIDENCE = ['high', 'medium', 'low'] as const;
export type ChangeImpactRequirementConfidence = (typeof CHANGE_IMPACT_REQUIREMENT_CONFIDENCE)[number];

export const ChangeImpactRequirementRelatedScenarioSchema = z.object({
  id: z.string().uuid(),
  title: z.string().max(300),
});
export type ChangeImpactRequirementRelatedScenario = z.infer<typeof ChangeImpactRequirementRelatedScenarioSchema>;

export const ChangeImpactRequirementRelatedFlowSchema = z.object({
  id: z.string().uuid(),
  name: z.string().max(300),
});
export type ChangeImpactRequirementRelatedFlow = z.infer<typeof ChangeImpactRequirementRelatedFlowSchema>;

export const ChangeImpactRequirementLinkSchema = z.object({
  key: z.string().max(80),
  title: z.string().max(300).default(''),
  /** Present when matched to quality_requirements. */
  requirementId: z.string().uuid().nullable(),
  /** Present when matched to story_links. */
  storyLinkId: z.string().uuid().nullable(),
  storySource: z.string().max(40).nullable().optional(),
  /** How we know about this key — may list multiple (deduped). */
  evidence: z.array(z.enum(CHANGE_IMPACT_REQUIREMENT_EVIDENCE)).min(1).max(5),
  confidence: z.enum(CHANGE_IMPACT_REQUIREMENT_CONFIDENCE),
  /** True when evidence is weak / unmatched — UI should say "Possible". */
  possible: z.boolean().default(false),
  relatedScenarioCount: z.number().int().nonnegative().default(0),
  relatedFlowCount: z.number().int().nonnegative().default(0),
  relatedScenarios: z.array(ChangeImpactRequirementRelatedScenarioSchema).max(12).default([]),
  relatedFlows: z.array(ChangeImpactRequirementRelatedFlowSchema).max(12).default([]),
});
export type ChangeImpactRequirementLink = z.infer<typeof ChangeImpactRequirementLinkSchema>;

export const ChangeImpactRequirementLinkageSchema = z.object({
  /** Explicit advisory marker — not causation. */
  attribution: z.literal('advisory_linkage'),
  parsedKeys: z.array(z.string().max(80)).max(20),
  linkedCount: z.number().int().nonnegative(),
  possibleCount: z.number().int().nonnegative(),
  items: z.array(ChangeImpactRequirementLinkSchema).max(30),
});
export type ChangeImpactRequirementLinkage = z.infer<typeof ChangeImpactRequirementLinkageSchema>;

/**
 * Feature 4C — coverage gap intelligence near a change (compose-on-read).
 * Relational only — never invents coverage from title match; never safe-skip (4D).
 */
export const COVERAGE_INTELLIGENCE_STATUS = ['available', 'limited', 'insufficient_evidence'] as const;
export type CoverageIntelligenceStatus = (typeof COVERAGE_INTELLIGENCE_STATUS)[number];

export const COVERAGE_REQUIREMENT_STATES = [
  'covered',
  'partially_covered',
  'scenario_only',
  'not_covered',
  'unknown',
] as const;
export type CoverageRequirementState = (typeof COVERAGE_REQUIREMENT_STATES)[number];

export const COVERAGE_GAP_KINDS = [
  'requirement_uncovered',
  'scenario_only',
  'partially_covered',
  'impacted_scenario_manual',
  'automation_no_recent_execution',
  'unknown_key',
] as const;
export type CoverageGapKind = (typeof COVERAGE_GAP_KINDS)[number];

export const ChangeImpactCoverageGapSchema = z.object({
  kind: z.enum(COVERAGE_GAP_KINDS),
  state: z.enum(COVERAGE_REQUIREMENT_STATES).optional(),
  key: z.string().max(80).optional(),
  title: z.string().max(300),
  requirementId: z.string().uuid().nullable().optional(),
  scenarioId: z.string().uuid().nullable().optional(),
  flowId: z.string().uuid().nullable().optional(),
  scenarioCount: z.number().int().nonnegative().default(0),
  automatedScenarioCount: z.number().int().nonnegative().default(0),
  flowCount: z.number().int().nonnegative().default(0),
  recentlyExecutedFlowCount: z.number().int().nonnegative().nullable().optional(),
  suggestedAction: z.string().max(300),
  href: z.string().max(500).optional(),
});
export type ChangeImpactCoverageGap = z.infer<typeof ChangeImpactCoverageGapSchema>;

export const ChangeImpactCoverageIntelligenceSchema = z.object({
  attribution: z.literal('coverage_intelligence'),
  status: z.enum(COVERAGE_INTELLIGENCE_STATUS),
  /** Near-change keys / linked requirements in scope. */
  nearChangeRequirementCount: z.number().int().nonnegative(),
  knownRequirementCount: z.number().int().nonnegative(),
  unknownKeyCount: z.number().int().nonnegative(),
  /** Counts of known (catalogue-matched) requirements by coverage state. */
  coveredCount: z.number().int().nonnegative(),
  partiallyCoveredCount: z.number().int().nonnegative(),
  scenarioOnlyCount: z.number().int().nonnegative(),
  notCoveredCount: z.number().int().nonnegative(),
  unknownCount: z.number().int().nonnegative(),
  /** Impacted scenarios lacking automation (separate from requirement coverage). */
  impactedScenarioCount: z.number().int().nonnegative(),
  impactedScenarioAutomatedCount: z.number().int().nonnegative(),
  impactedScenarioManualCount: z.number().int().nonnegative(),
  /** Execution evidence for flows linked to near-change covered/partial requirements (14d). */
  linkedFlowCount: z.number().int().nonnegative(),
  recentlyExecutedLinkedFlowCount: z.number().int().nonnegative().nullable(),
  recentExecutionKnown: z.boolean(),
  /** Project-wide catalogue snapshot (not the change %). */
  projectRequirementTotal: z.number().int().nonnegative(),
  projectRequirementWithScenario: z.number().int().nonnegative(),
  projectRequirementUncovered: z.number().int().nonnegative(),
  gaps: z.array(ChangeImpactCoverageGapSchema).max(20).default([]),
  limitations: z.array(z.string().max(300)).max(12).default([]),
  evidenceNotes: z.array(z.string().max(300)).max(12).default([]),
  claimsCompleteCoverage: z.literal(false),
  claimsSafeSkip: z.literal(false),
  claimsPercentWithoutDenominator: z.literal(false),
});
export type ChangeImpactCoverageIntelligence = z.infer<typeof ChangeImpactCoverageIntelligenceSchema>;

/**
 * Feature 4D — regression decision / safe-skip narrative (advisory only).
 * SAFE_TO_SKIP is reserved and must not be emitted until a positive safety
 * threshold exists; v1 defaults to INSUFFICIENT_EVIDENCE for non-risk items.
 */
export const REGRESSION_DECISIONS = [
  'run_recommended',
  'review_required',
  'insufficient_evidence',
  'safe_to_skip',
] as const;
export type RegressionDecision = (typeof REGRESSION_DECISIONS)[number];

export const REGRESSION_EVIDENCE_STRENGTH = ['none', 'weak', 'moderate', 'strong'] as const;
export type RegressionEvidenceStrength = (typeof REGRESSION_EVIDENCE_STRENGTH)[number];

export const ChangeImpactRegressionDecisionItemSchema = z.object({
  kind: z.enum(['flow', 'scenario', 'not_selected']),
  id: z.string().max(80),
  title: z.string().max(300),
  decision: z.enum(REGRESSION_DECISIONS),
  evidenceStrength: z.enum(REGRESSION_EVIDENCE_STRENGTH),
  why: z.array(z.string().max(300)).max(8).default([]),
  href: z.string().max(500).optional(),
});
export type ChangeImpactRegressionDecisionItem = z.infer<typeof ChangeImpactRegressionDecisionItemSchema>;

export const ChangeImpactRegressionDecisionSchema = z.object({
  attribution: z.literal('regression_risk_advisory'),
  status: z.enum(['available', 'limited', 'insufficient_evidence']),
  overall: z.enum(REGRESSION_DECISIONS),
  evidenceStrength: z.enum(REGRESSION_EVIDENCE_STRENGTH),
  runRecommendedCount: z.number().int().nonnegative(),
  reviewRequiredCount: z.number().int().nonnegative(),
  insufficientEvidenceCount: z.number().int().nonnegative(),
  /** Expect 0 until positive safety threshold is product-approved. */
  safeToSkipCount: z.number().int().nonnegative(),
  items: z.array(ChangeImpactRegressionDecisionItemSchema).max(20).default([]),
  limitations: z.array(z.string().max(300)).max(12).default([]),
  evidenceNotes: z.array(z.string().max(300)).max(12).default([]),
  /** Product locks — 4D never silently skips or claims CI authority. */
  claimsSafeSkip: z.literal(false),
  claimsCiAuthority: z.literal(false),
  emitsSafeToSkip: z.literal(false),
});
export type ChangeImpactRegressionDecision = z.infer<typeof ChangeImpactRegressionDecisionSchema>;

export const ChangeImpactResponseSchema = z.object({
  projectId: z.string().uuid(),
  openFailureCount: z.number().int().nonnegative(),
  scenarios: z.array(ChangeImpactScenarioSchema),
  /** Automation flows impacted — present even when no scenarios exist. */
  flows: z.array(ChangeImpactFlowSchema).default([]),
  review: ChangeReviewSchema.nullable(),
  /** Convenience mirror of review.findings.maintenance for the queue UI. */
  maintenance: ChangeImpactMaintenanceSchema.nullable().optional(),
  /** Phase 5D.1 — persistent ownership rows (not findings JSON). */
  maintenanceItems: z.array(MaintenanceItemSchema).default([]),
  /**
   * Feature 2B — open maintenance ownership across the project (Change Impact surface).
   * Not a second inbox — discoverability + assigned-to-me counts only.
   */
  maintenanceQueue: MaintenanceOwnershipQueueSchema.nullable().optional(),
  /** #16 developer packaging — compose of impact + #14/#15; same review artefact. */
  developer: DeveloperIntelSchema.nullable().optional(),
  /** #17 production packaging — compose of prod signal + #14/#15; same review artefact. */
  production: ProductionIntelSchema.nullable().optional(),
  /**
   * Feature 1A — related historical FI for impacted scenarios/flows.
   * Null when there is no impact scope to correlate (empty TIA / no review targets).
   */
  historicalEvidence: ChangeImpactHistoricalEvidenceSchema.nullable().optional(),
  /**
   * Feature 1B — commit/PR ticket keys → requirements / stories.
   * Null when there is no change review to parse.
   */
  requirementLinkage: ChangeImpactRequirementLinkageSchema.nullable().optional(),
  /**
   * Feature 1C — automation impact explainability summary.
   * Null when there is no change review; use analysisStatus for empty-graph honesty.
   */
  automationImpact: ChangeImpactAutomationImpactSchema.nullable().optional(),
  /**
   * Capability #5 — locator/selector-level automation impact (compose-on-read).
   * Advisory; never auto-heals or mutates locators.
   */
  locatorAutomationImpact: LocatorAutomationImpactSchema.nullable().optional(),
  /**
   * Capability #20 — test data ↔ impact / coverage soft-join (compose-on-read).
   * Advisory; never mutates test data or claims safe-skip.
   */
  testDataImpact: TestDataImpactIntelligenceSchema.nullable().optional(),
  /**
   * Feature 4B — selected vs full-suite metrics (compose-on-read).
   * Null only on unexpected compose failure; prefer status bands inside the object.
   */
  selectionMetrics: ChangeImpactSelectionMetricsSchema.nullable().optional(),
  /**
   * Feature 4C — coverage gaps near change (compose-on-read).
   * Separates requirement / scenario / automation / execution — never invents %.
   */
  coverageIntelligence: ChangeImpactCoverageIntelligenceSchema.nullable().optional(),
  /**
   * Feature 4D — regression decision narrative (advisory).
   * Does not emit safe_to_skip until a positive safety threshold exists.
   */
  regressionDecision: ChangeImpactRegressionDecisionSchema.nullable().optional(),
  /**
   * Feature 1D — recent contract-check failures (schedule drift), not commit-caused.
   */
  contractDrift: ChangeImpactContractDriftSchema.nullable().optional(),
  /**
   * Wave 5A — visual evidence soft-join (deep links). Advisory only.
   */
  visualEvidence: ChangeImpactVisualEvidenceSchema.nullable().optional(),
  /**
   * Feature 1E — pipeline apply health from the current review (if stamped).
   */
  applySignal: ChangeImpactApplyPipelineSchema.nullable().optional(),
  /**
   * Feature 2A — why maintenance is (or is not) recommended.
   * Advisory; never claims the commit broke a test.
   */
  maintenanceExplain: ChangeImpactMaintenanceExplainSchema.nullable().optional(),
  /**
   * Feature 2C — bounded healing draft hints for impacted flows (advisory; no write).
   */
  maintenanceDraftHints: z.array(MaintenanceDraftHintSchema).max(20).optional(),
});
export type ChangeImpactResponse = z.infer<typeof ChangeImpactResponseSchema>;

export const ChangeReviewActionResponseSchema = z.object({
  review: ChangeReviewSchema,
  created: z.number().int().nonnegative().optional(),
  started: z.number().int().nonnegative().optional(),
  skipped: z.number().int().nonnegative().optional(),
});
export type ChangeReviewActionResponse = z.infer<typeof ChangeReviewActionResponseSchema>;

export const MaintenanceDecisionRequestSchema = z.object({
  decision: z.enum(MAINTENANCE_DECISIONS),
  note: z.string().max(2_000).optional(),
});
export type MaintenanceDecisionRequest = z.infer<typeof MaintenanceDecisionRequestSchema>;

export const MaintenanceDecisionResponseSchema = z.object({
  review: ChangeReviewSchema,
  started: z.number().int().nonnegative().optional(),
  skipped: z.number().int().nonnegative().optional(),
  retestRunIds: z.array(z.string().uuid()).optional(),
});
export type MaintenanceDecisionResponse = z.infer<typeof MaintenanceDecisionResponseSchema>;

/**
 * Deterministic severity from TIA scores + affected asset counts.
 * Shared by API (and future CLI) — no AI required.
 */
export function deriveImpactLevel(input: { maxScore: number; scenarioCount: number; flowCount: number }): ImpactLevel {
  const { maxScore, scenarioCount, flowCount } = input;
  if (flowCount === 0 && scenarioCount === 0) return 'none';
  if (maxScore >= 0.85 || scenarioCount >= 8 || flowCount >= 8) return 'critical';
  if (maxScore >= 0.55 || scenarioCount >= 3 || flowCount >= 3) return 'high';
  if (maxScore >= 0.25 || scenarioCount >= 1 || flowCount >= 1) return 'medium';
  return 'low';
}

export function deriveImpactConfidence(input: { maxScore: number; mappedFileRatio: number }): ImpactConfidence {
  if (input.maxScore >= 0.55 && input.mappedFileRatio >= 0.5) return 'high';
  if (input.maxScore >= 0.25 || input.mappedFileRatio >= 0.25) return 'medium';
  return 'low';
}

export function impactReviewRecommended(level: ImpactLevel): boolean {
  return level === 'medium' || level === 'high' || level === 'critical';
}

/** #15 — derive maintenance recommendation from classified target counts. */
export function deriveMaintenanceRecommendation(input: {
  manualCount: number;
  bothCount: number;
  automationOnlyCount: number;
}): MaintenanceRecommendation {
  const { manualCount, bothCount, automationOnlyCount } = input;
  if (bothCount > 0 || (manualCount > 0 && automationOnlyCount > 0)) {
    return 'manual_and_automation_review';
  }
  if (manualCount > 0) return 'manual_review';
  if (automationOnlyCount > 0) return 'automation_review';
  return 'none';
}

export const RunAffectedRequestSchema = z.object({
  scenarioIds: z.array(z.string().uuid()).min(1).max(BULK_SCENARIO_MARK_MAX),
});
export type RunAffectedRequest = z.infer<typeof RunAffectedRequestSchema>;

export const RunAffectedResponseSchema = z.object({
  started: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type RunAffectedResponse = z.infer<typeof RunAffectedResponseSchema>;

export const IMPORT_SCENARIO_MAX = 500;

export const ImportScenarioRowSchema = z.object({
  title: z.string().trim().min(1).max(200),
  businessFlowId: z.string().uuid(),
  requirementId: z.string().max(80).nullable().optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
});
export type ImportScenarioRow = z.infer<typeof ImportScenarioRowSchema>;

export const ImportScenariosRequestSchema = z.object({
  rows: z.array(ImportScenarioRowSchema).min(1).max(IMPORT_SCENARIO_MAX),
});
export type ImportScenariosRequest = z.infer<typeof ImportScenariosRequestSchema>;

export const ImportScenariosResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  scenarios: z.array(QualityScenarioSchema),
});
export type ImportScenariosResponse = z.infer<typeof ImportScenariosResponseSchema>;

export const ScenarioStepResultSchema = z.object({
  order: z.number().int().positive(),
  status: z.enum(SCENARIO_STEP_RESULT),
  notes: z.string(),
  expected: z.string().default(''),
  actual: z.string().default(''),
  evidence: z.array(ScenarioEvidenceSchema).default([]),
  environment: z.string().default(''),
  browser: z.string().default(''),
  build: z.string().default(''),
});
export type ScenarioStepResult = z.infer<typeof ScenarioStepResultSchema>;

export const ScenarioExecutionCountsSchema = z.object({
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  untested: z.number().int().nonnegative(),
});
export type ScenarioExecutionCounts = z.infer<typeof ScenarioExecutionCountsSchema>;

export const ScenarioExecutionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Null for automation-first executions without a Quality Scenario link. */
  scenarioId: z.string().uuid().nullable(),
  scenarioTitle: z.string(),
  domain: z.string(),
  module: z.string(),
  businessFlow: z.string(),
  steps: z.array(ScenarioStepSchema),
  stepResults: z.array(ScenarioStepResultSchema),
  currentIndex: z.number().int().nonnegative(),
  status: z.enum(SCENARIO_EXECUTION_STATUS),
  statusOverride: z.enum(SCENARIO_EXECUTION_RESULT).nullable().default(null),
  counts: ScenarioExecutionCountsSchema,
  evidenceCount: z.number().int().nonnegative(),
  attachments: z.array(ScenarioEvidenceSchema).max(MAX_SCENARIO_ATTACHMENTS).default([]),
  comment: z.string().max(4_000).default(''),
  failures: z.array(QualityFailureSchema).default([]),
  history: z.array(ScenarioExecutionHistoryItemSchema).default([]),
  /** Requirement #9 — manual grade vs automation orchestration. */
  executionKind: z.enum(['manual', 'automation']).default('manual'),
  automationFlowId: z.string().uuid().nullable().default(null),
  runId: z.string().uuid().nullable().default(null),
  scenarioVersion: z.number().int().positive().nullable().default(null),
  /** Phase 5E.2/5E.4 — from test_runs.ran_against when linked; null for legacy/manual. */
  ranAgainst: z.string().max(80).nullable().optional().default(null),
  /** Phase 5E.4 — from test_runs.execution_target_kind when linked. */
  executionTargetKind: z.enum(['saas', 'agent', 'cloud', 'explicit']).nullable().optional().default(null),
  /** Phase 5E.4 — true when a Playwright/registry trace exists for runId. */
  hasTrace: z.boolean().optional().default(false),
  triggeredBy: z.string().uuid().nullable().default(null),
  failureSummary: z.string().max(2_000).default(''),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type ScenarioExecution = z.infer<typeof ScenarioExecutionSchema>;

export const ScenarioExecutionResponseSchema = z.object({
  execution: ScenarioExecutionSchema,
});
export type ScenarioExecutionResponse = z.infer<typeof ScenarioExecutionResponseSchema>;

export const ScenarioExecutionListItemSchema = z.object({
  id: z.string().uuid(),
  scenarioId: z.string().uuid(),
  scenarioTitle: z.string(),
  domain: z.string(),
  module: z.string(),
  businessFlow: z.string(),
  status: z.enum(SCENARIO_EXECUTION_STATUS),
  build: z.string(),
  executionKind: z.enum(['manual', 'automation']).default('manual'),
  automationFlowId: z.string().uuid().nullable().optional(),
  runId: z.string().uuid().nullable().optional(),
  /** Persisted on quality_scenario_executions.scenario_version (nullable for legacy rows). */
  scenarioVersion: z.number().int().positive().nullable().optional(),
  ranAgainst: z.string().max(80).nullable().optional(),
  executionTargetKind: z.enum(['saas', 'agent', 'cloud', 'explicit']).nullable().optional(),
  hasTrace: z.boolean().optional(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  /** Requirement #10 — optional intelligence filter facets. */
  failureClass: z.string().nullable().optional(),
  hadHealing: z.boolean().optional(),
  hadRetry: z.boolean().optional(),
  browser: z.string().optional(),
  environment: z.string().optional(),
  triggeredBy: z.string().uuid().nullable().optional(),
  automationStability: z.string().nullable().optional(),
});
export type ScenarioExecutionListItem = z.infer<typeof ScenarioExecutionListItemSchema>;

export const ScenarioExecutionListResponseSchema = z.object({
  executions: z.array(ScenarioExecutionListItemSchema),
});
export type ScenarioExecutionListResponse = z.infer<typeof ScenarioExecutionListResponseSchema>;

function refineFailedStep(value: { status: string; notes?: string; actual?: string }, ctx: z.RefinementCtx): void {
  if (value.status !== 'failed') return;
  if (!(value.notes || '').trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['notes'], message: 'required' });
  }
  if (!(value.actual || '').trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['actual'], message: 'required' });
  }
}

export const RecordScenarioStepRequestSchema = z
  .object({
    status: z.enum(['passed', 'failed', 'blocked', 'skipped']),
    order: z.number().int().positive().optional(),
    notes: z.string().max(2_000).optional(),
    expected: z.string().max(500).optional(),
    actual: z.string().max(2_000).optional(),
    evidence: z.array(ScenarioEvidenceSchema).max(10).optional(),
    environment: z.string().max(80).optional(),
    browser: z.string().max(80).optional(),
    build: z.string().max(80).optional(),
  })
  .superRefine(refineFailedStep);
export type RecordScenarioStepRequest = z.infer<typeof RecordScenarioStepRequestSchema>;

/** `null` hands the run status back to the step results. */
export const SetExecutionStatusRequestSchema = z.object({
  status: z.enum(SCENARIO_EXECUTION_RESULT).nullable(),
  comment: z.string().max(4_000).optional(),
});
export type SetExecutionStatusRequest = z.infer<typeof SetExecutionStatusRequestSchema>;

export const BatchRecordScenarioStepsRequestSchema = z
  .object({
    orders: z.array(z.number().int().positive()).min(1).max(200),
    status: z.enum(['passed', 'failed', 'blocked', 'skipped']),
    notes: z.string().max(2_000).optional(),
    expected: z.string().max(500).optional(),
    actual: z.string().max(2_000).optional(),
    environment: z.string().max(80).optional(),
    browser: z.string().max(80).optional(),
    build: z.string().max(80).optional(),
  })
  .superRefine(refineFailedStep);
export type BatchRecordScenarioStepsRequest = z.infer<typeof BatchRecordScenarioStepsRequestSchema>;

export const PatchExecutionRequestSchema = z
  .object({
    attachments: z.array(ScenarioEvidenceSchema).max(MAX_SCENARIO_ATTACHMENTS).optional(),
    comment: z.string().max(4_000).optional(),
  })
  .refine((value) => value.attachments !== undefined || value.comment !== undefined);
export type PatchExecutionRequest = z.infer<typeof PatchExecutionRequestSchema>;

export const PatchExecutionStepRequestSchema = z
  .object({
    order: z.number().int().positive(),
    notes: z.string().max(2_000).optional(),
    actual: z.string().max(2_000).optional(),
    evidence: z.array(ScenarioEvidenceSchema).max(10).optional(),
  })
  .refine((value) => value.notes !== undefined || value.actual !== undefined || value.evidence !== undefined);
export type PatchExecutionStepRequest = z.infer<typeof PatchExecutionStepRequestSchema>;

export const ModuleFlowCoverageSchema = z.object({
  flowId: z.string().uuid(),
  name: z.string(),
  scenarioCount: z.number().int().nonnegative(),
  automatedCount: z.number().int().nonnegative(),
  coveragePercent: z.number().min(0).max(100).nullable(),
});
export type ModuleFlowCoverage = z.infer<typeof ModuleFlowCoverageSchema>;

export const ModulePageResponseSchema = z.object({
  module: QualityModuleSchema.extend({
    domainId: z.string().uuid(),
    domainName: z.string(),
  }),
  qualityScore: z.number().int().min(0).max(100).nullable(),
  risk: z.enum(QUALITY_RISK_SEVERITIES).nullable(),
  scenarioCount: z.number().int().nonnegative(),
  manualCount: z.number().int().nonnegative(),
  automatedCount: z.number().int().nonnegative(),
  needsReviewCount: z.number().int().nonnegative(),
  flows: z.array(ModuleFlowCoverageSchema),
});
export type ModulePageResponse = z.infer<typeof ModulePageResponseSchema>;

export function splitGherkinLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Render structured sequence as Gherkin; component refs become [KEY] lines. */
export function stepsToGherkin(steps: ScenarioSequenceItem[]): string {
  return steps
    .map((step) => {
      if (isScenarioComponentReference(step)) {
        return `[${step.componentKey}]`;
      }
      if (!step.action.trim()) return '';
      const kind = step.kind ?? 'when';
      return `${GHERKIN_KEYWORD_LABEL[kind]} ${step.action.trim()}`;
    })
    .filter(Boolean)
    .join('\n');
}

/** Build the step list snapshotted into a manual execution. Procedural steps win; pure Gherkin expands by line. */
export function buildExecutionStepsFromScenario(input: {
  given: string;
  when: string;
  then: string;
  steps: ScenarioSequenceItem[];
}): ScenarioStep[] {
  const procedural = input.steps
    .filter(isScenarioNormalStep)
    .map((step) => ({
      ...step,
      action: step.action.trim(),
      expectedResult: step.expectedResult.trim(),
    }))
    .filter((step) => step.action.length > 0);

  if (procedural.length > 0) {
    return procedural.map((step, index) =>
      finalizeScenarioStep(
        {
          ...step,
          action: step.action,
          expectedResult: step.expectedResult,
          inputData: (step.inputData || step.testData || '').trim(),
          testData: (step.testData || step.inputData || '').trim(),
          attachments: step.attachments ?? [],
        },
        index + 1,
      ),
    );
  }

  const out: ScenarioStep[] = [];
  let order = 1;
  for (const line of splitGherkinLines(input.given)) {
    out.push(finalizeScenarioStep({ kind: 'given', action: line, expectedResult: '' }, order++));
  }
  for (const line of splitGherkinLines(input.when)) {
    out.push(finalizeScenarioStep({ kind: 'when', action: line, expectedResult: '' }, order++));
  }
  for (const line of splitGherkinLines(input.then)) {
    out.push(finalizeScenarioStep({ kind: 'then', action: line, expectedResult: line }, order++));
  }
  return out;
}

export function hasRunnableScenarioSteps(input: {
  given: string;
  when: string;
  then: string;
  steps: ScenarioSequenceItem[];
}): boolean {
  return buildExecutionStepsFromScenario(input).length > 0;
}

/** Untested steps still open on a live execution. Already recorded results are left alone. */
export function remainingUntestedStepOrders(execution: {
  stepResults: Array<{ order: number; status: string }>;
}): number[] {
  return execution.stepResults.filter((row) => row.status === 'untested').map((row) => row.order);
}

export const SCENARIO_VERSION_REASONS = ['edit', 'approve', 'fork', 'request_review'] as const;
export type ScenarioVersionReason = (typeof SCENARIO_VERSION_REASONS)[number];

export const ScenarioVersionSchema = z.object({
  id: z.string().uuid(),
  scenarioId: z.string().uuid(),
  version: z.number().int().positive(),
  title: z.string(),
  given: z.string(),
  when: z.string(),
  then: z.string(),
  steps: z.array(ScenarioSequenceItemSchema),
  status: z.enum(SCENARIO_STATUS),
  reason: z.enum(SCENARIO_VERSION_REASONS),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type ScenarioVersion = z.infer<typeof ScenarioVersionSchema>;

export const ScenarioVersionListResponseSchema = z.object({
  versions: z.array(ScenarioVersionSchema),
});
export type ScenarioVersionListResponse = z.infer<typeof ScenarioVersionListResponseSchema>;

export const ScenarioReviewResponseSchema = z.object({
  scenario: QualityScenarioSchema,
  review: ScenarioReviewSchema,
});
export type ScenarioReviewResponse = z.infer<typeof ScenarioReviewResponseSchema>;

export const RequestScenarioReviewBodySchema = z.object({
  comment: z.string().max(2000).optional(),
});
export type RequestScenarioReviewBody = z.infer<typeof RequestScenarioReviewBodySchema>;

export const DecideScenarioReviewBodySchema = z.object({
  decision: z.enum(['approved', 'changes_requested']),
  comment: z.string().max(2000).optional(),
});
export type DecideScenarioReviewBody = z.infer<typeof DecideScenarioReviewBodySchema>;

export const CONVERT_SOURCES = [
  'testim',
  'jira',
  'tcm',
  'playwright',
  'cypress',
  'selenium',
  'gherkin',
  'postman',
] as const;
export type ConvertSource = (typeof CONVERT_SOURCES)[number];

export const CONVERT_CONTENT_MAX = 400_000;
export const CONVERT_ROW_MAX = 200;

export const ConvertCanvasActionSchema = z.object({
  type: z.enum(['navigate', 'click', 'fill', 'assert', 'api-request']),
  title: z.string().min(1).max(200),
  target: z.string().max(500).default(''),
  value: z.string().max(2_000).default(''),
});
export type ConvertCanvasAction = z.infer<typeof ConvertCanvasActionSchema>;

export const ConvertPreviewRowSchema = z.object({
  externalId: z.string().min(1).max(120),
  title: z.string().min(1).max(200),
  given: z.string().max(2_000).default(''),
  when: z.string().max(2_000).default(''),
  then: z.string().max(2_000).default(''),
  steps: z
    .array(
      z.object({
        order: z.number().int().positive().optional(),
        kind: z.enum(SCENARIO_STEP_KIND).optional(),
        action: z.string().min(1).max(500),
        expectedResult: z.string().max(500).optional(),
      }),
    )
    .max(50)
    .default([]),
  requirementId: z.string().max(80).nullable().optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
  tags: z.array(z.string().max(40)).max(20).default([]),
  folderPath: z.array(z.string().min(1).max(80)).max(3).default([]),
  hasAutomation: z.boolean().default(false),
  canvasActions: z.array(ConvertCanvasActionSchema).max(50).default([]),
  existingStatus: z.enum(SCENARIO_STATUS).nullable().optional(),
});
export type ConvertPreviewRow = z.infer<typeof ConvertPreviewRowSchema>;

export const ConvertPreviewRequestSchema = z.object({
  source: z.enum(CONVERT_SOURCES),
  fileName: z.string().max(200).optional(),
  content: z.string().min(1).max(CONVERT_CONTENT_MAX),
});
export type ConvertPreviewRequest = z.infer<typeof ConvertPreviewRequestSchema>;

export const ConvertPreviewResponseSchema = z.object({
  source: z.enum(CONVERT_SOURCES),
  rows: z.array(ConvertPreviewRowSchema).max(CONVERT_ROW_MAX),
  warnings: z.array(z.string()),
});
export type ConvertPreviewResponse = z.infer<typeof ConvertPreviewResponseSchema>;

export const ConvertConfirmRequestSchema = z.object({
  source: z.enum(CONVERT_SOURCES),
  rows: z.array(ConvertPreviewRowSchema).min(1).max(CONVERT_ROW_MAX),
});
export type ConvertConfirmRequest = z.infer<typeof ConvertConfirmRequestSchema>;

export const ConvertConfirmResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  scenarios: z.array(QualityScenarioSchema),
});
export type ConvertConfirmResponse = z.infer<typeof ConvertConfirmResponseSchema>;

export const ScenarioPassRateTrendResponseSchema = z.object({
  days: z.array(ScenarioPassRateTrendDaySchema),
});
export type ScenarioPassRateTrendResponse = z.infer<typeof ScenarioPassRateTrendResponseSchema>;

/** Overview Execution Trends — QSE activity by kind (not pass-rate, not Proven). */
export const EXECUTION_KIND_TREND_DAYS = [7, 30, 90] as const;
export type ExecutionKindTrendDays = (typeof EXECUTION_KIND_TREND_DAYS)[number];

export function isExecutionKindTrendDays(value: number): value is ExecutionKindTrendDays {
  return (EXECUTION_KIND_TREND_DAYS as readonly number[]).includes(value);
}

/**
 * One calendar day of completed scenario executions, broken down two ways.
 *
 * Both breakdowns describe the SAME executions, so they must never be summed
 * together or presented as four independent series:
 * - By method: `manual` + `automation` is every completed execution that day.
 * - By outcome: `passed` + `failed` is a SUBSET — blocked, skipped and
 *   cancelled executions are counted in the method totals but in neither
 *   outcome counter.
 *
 * `passed` here is an execution outcome. Passed is not Proven.
 */
export const ExecutionKindTrendPointSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  manual: z.number().int().nonnegative(),
  automation: z.number().int().nonnegative(),
  /** Latest-outcome-agnostic count of executions that completed as passed. */
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type ExecutionKindTrendPoint = z.infer<typeof ExecutionKindTrendPointSchema>;

export const ExecutionKindTrendResponseSchema = z.object({
  days: z.union([z.literal(7), z.literal(30), z.literal(90)]),
  points: z.array(ExecutionKindTrendPointSchema),
});
export type ExecutionKindTrendResponse = z.infer<typeof ExecutionKindTrendResponseSchema>;

/**
 * Overview Quality Snapshot — latest terminal QSE step outcomes (execution metric only).
 * Pass Rate = passed / (passed + failed); skipped excluded from denominator.
 */
export const ExecutionStepSnapshotResponseSchema = z.object({
  totalSteps: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  /** null when passed + failed === 0 (mathematically undefined). */
  passRate: z.number().nonnegative().nullable(),
});
export type ExecutionStepSnapshotResponse = z.infer<typeof ExecutionStepSnapshotResponseSchema>;

export const GenerateScenarioStepsRequestSchema = z.object({
  prompt: z.string().trim().min(1).max(2_000),
  businessFlowId: z.string().uuid().optional(),
  scenarioTitle: z.string().max(200).optional(),
  /** Optional existing scenario context (project-scoped). Never overwritten by AI. */
  scenarioId: z.string().uuid().optional(),
  requirementId: z.string().uuid().optional(),
});
export type GenerateScenarioStepsRequest = z.infer<typeof GenerateScenarioStepsRequestSchema>;

export const GeneratedScenarioStepSchema = z.object({
  kind: z.enum(SCENARIO_STEP_KIND),
  action: z.string().min(1).max(500),
  inputData: z.string().max(2_000).optional(),
  expectedResult: z.string().max(500).optional(),
});
export type GeneratedScenarioStep = z.infer<typeof GeneratedScenarioStepSchema>;

export const AiStepSuggestionSchema = z.object({
  id: z.string().min(1).max(40),
  label: z.string().min(1).max(200),
});
export type AiStepSuggestionWire = z.infer<typeof AiStepSuggestionSchema>;

/**
 * Phase 8.5.4 — optional advisory evaluation on generate responses.
 * Inline (not imported from scenario-evaluation) to avoid a contracts cycle:
 * scenario-evaluation → scenarios → scenario-evaluation.
 */
const ScenarioAiEvaluationWireSchema = z.object({
  overall: z.number().min(0).max(1),
  structureScore: z.number().min(0).max(1),
  variantScore: z.number().min(0).max(1),
  traceabilityScore: z.number().min(0).max(1),
  groundingScore: z.number().min(0).max(1),
  hygieneScore: z.number().min(0).max(1),
  findings: z
    .array(
      z
        .object({
          code: z.string().trim().min(1).max(80),
          severity: z.enum(['info', 'warn', 'block']),
          message: z.string().trim().min(1).max(500),
        })
        .passthrough(),
    )
    .max(100),
  grounding: z.object({
    requirementCovered: z.boolean(),
    acceptanceCriteriaCovered: z.number().int().min(0),
    acceptanceCriteriaTotal: z.number().int().min(0),
    unsupportedClaims: z.number().int().min(0),
    evidence: z.array(z.record(z.string(), z.unknown())).max(100),
  }),
  testabilityHint: z.object({
    suitability: z.enum(['manual', 'partial', 'automatable', 'unknown']),
    convertReadyHint: z.boolean(),
    summary: z.string().max(300),
  }),
  convertReadyHint: z.boolean(),
  highestSeverity: z.enum(['info', 'warn', 'block']),
});

export const GenerateScenarioStepsResponseSchema = z.object({
  steps: z.array(GeneratedScenarioStepSchema),
  suggestions: z.array(AiStepSuggestionSchema),
  source: z.enum(['llm', 'heuristic']),
  /** Advisory evaluation — never approves or persists. Optional for older clients. */
  evaluation: ScenarioAiEvaluationWireSchema.optional(),
});
export type GenerateScenarioStepsResponse = z.infer<typeof GenerateScenarioStepsResponseSchema>;

export const AI_DESIGN_MODE = ['new', 'variants', 'improve'] as const;
export type AiDesignMode = (typeof AI_DESIGN_MODE)[number];

export const GenerateScenarioDraftsRequestSchema = z.object({
  prompt: z.string().trim().min(1).max(2_000),
  businessFlowId: z.string().uuid().optional(),
  /** Seed from an existing project requirement (UUID). */
  requirementId: z.string().uuid().optional(),
  /** Existing scenario context — AI proposes drafts; never auto-overwrites. */
  scenarioId: z.string().uuid().optional(),
  mode: z.enum(AI_DESIGN_MODE).optional(),
  /**
   * Phase 8.5.7 — optional Story/Jira AC text for deterministic grounding.
   * Evaluated once inside generateScenarioDraftsWithAi; not a second LLM call.
   */
  acceptanceCriteria: z.string().max(8_000).optional(),
  /** Optional free-form story/requirement body for grounding (Story paste / Jira). */
  requirementText: z.string().max(16_000).optional(),
});
export type GenerateScenarioDraftsRequest = z.infer<typeof GenerateScenarioDraftsRequestSchema>;

export const AiDraftDuplicateHintSchema = z.object({
  scenarioId: z.string().uuid(),
  title: z.string(),
  reason: z.enum(['title', 'similar_title']),
});
export type AiDraftDuplicateHint = z.infer<typeof AiDraftDuplicateHintSchema>;

export const AiDraftProvenanceSchema = z.object({
  generatedBy: z.literal('ai'),
  generationSource: z.literal('scenario-ai'),
  source: z.enum(['llm', 'heuristic', 'deterministic']),
  generatedAt: z.string(),
  /** Provider/model label only — never API keys. */
  model: z.string().max(120).optional(),
  sourceRequirementIds: z.array(z.string().uuid()).max(10).optional(),
  sourceScenarioIds: z.array(z.string().uuid()).max(10).optional(),
  /** Feature #3 — confirmed repo knowledge proposals that grounded this draft. */
  sourceRepoProposalIds: z.array(z.string().uuid()).max(12).optional(),
  sourceFilePaths: z.array(z.string().max(500)).max(20).optional(),
  extractorIds: z.array(z.string().max(80)).max(20).optional(),
});
export type AiDraftProvenance = z.infer<typeof AiDraftProvenanceSchema>;

export const GeneratedScenarioDraftSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2_000).optional(),
  priority: z.enum(SCENARIO_PRIORITY).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  preconditions: z.array(z.string().max(300)).max(20).optional(),
  given: z.string().max(2_000).optional(),
  when: z.string().max(2_000).optional(),
  then: z.string().max(2_000).optional(),
  steps: z.array(GeneratedScenarioStepSchema).min(1),
  /** Relationship-only requirement keys (e.g. REQ-001). Never scenario keys / never invent UUIDs. */
  requirementKeys: z.array(z.string().trim().min(1).max(80)).max(10).optional(),
  /** Keys that did not resolve in the project — advisory only. */
  unresolvedRequirementKeys: z.array(z.string().trim().min(1).max(80)).max(10).optional(),
  /** Resolved requirement UUIDs for this draft (project-scoped). */
  resolvedRequirementIds: z.array(z.string().uuid()).max(10).optional(),
  /** Existing step-component keys only (validated). */
  componentKeys: z.array(z.string().trim().min(1).max(80)).max(10).optional(),
  /** Unresolved component key suggestions. */
  unresolvedComponentKeys: z.array(z.string().trim().min(1).max(80)).max(10).optional(),
  /** Test-data tokens only, e.g. ${LoginUsers.username} — never resolved values. */
  testDataRefs: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
  automationSuitability: z.enum(['manual', 'partial', 'automatable']).optional(),
  variantKind: z.enum(['happy', 'negative', 'edge', 'boundary', 'other']).optional(),
  confidence: z.enum(['low', 'medium', 'high']).optional(),
  possibleDuplicates: z.array(AiDraftDuplicateHintSchema).max(5).optional(),
});
export type GeneratedScenarioDraftWire = z.infer<typeof GeneratedScenarioDraftSchema>;

export const GenerateScenarioDraftsResponseSchema = z.object({
  drafts: z.array(GeneratedScenarioDraftSchema),
  source: z.enum(['llm', 'heuristic']),
  provenance: AiDraftProvenanceSchema,
  /** Advisory evaluation — never approves or persists. Optional for older clients. */
  evaluation: ScenarioAiEvaluationWireSchema.optional(),
});
export type GenerateScenarioDraftsResponse = z.infer<typeof GenerateScenarioDraftsResponseSchema>;

export const FailureInsightRequestSchema = z.object({
  expected: z.string().max(2_000).optional(),
  actual: z.string().max(2_000).optional(),
  notes: z.string().max(4_000).optional(),
  stepAction: z.string().max(500).optional(),
});
export type FailureInsightRequest = z.infer<typeof FailureInsightRequestSchema>;

export const FailureInsightResponseSchema = z.object({
  analysis: z.string(),
  possibleCause: z.string(),
  source: z.enum(['llm', 'heuristic']),
});
export type FailureInsightResponse = z.infer<typeof FailureInsightResponseSchema>;
